import type { SiteLang } from "@/hooks/useLanguage";

export interface RAOfferingI18n {
  title: string;
  subtitle: string;
  description: string;
  whatTitle: string;
  whatParagraphs: string[];
  whyTitle: string;
  whyIntro: string;
  whyBenefits: { title: string; text: string }[];
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
  extraSections?: { title: string; paragraphs: string[] }[];
  helpCards?: { heading: string; intro: string; cards: { title: string; text: string }[] };
  processTitle?: string;
  processIntro?: string;
  processSteps?: { title: string; text: string }[];
  faqItems?: { q: string; a: string }[];
}

const raOfferingsEs: Record<string, RAOfferingI18n> = {
  /* ───────── Regulatory Strategy (Pharma) ───────── */
  "regulatory-strategy-pharma": {
    title: "Estrategia regulatoria para Pharma",
    subtitle: "Diseñando un concepto para llevar tus productos farmacéuticos al mercado",
    description: "Establecer una estrategia regulatoria implica elegir el procedimiento adecuado para un tipo de producto, analizar las regulaciones y requisitos locales, seleccionar en qué países lanzar y asesorar sobre el tiempo hasta la comercialización.",
    whatTitle: "¿Qué es la estrategia regulatoria?",
    whatParagraphs: [
      "<strong class='text-foreground'>Diseñar un concepto para llevar tus productos farmacéuticos al mercado.</strong>",
      "Establecer una estrategia regulatoria implica <strong class='text-foreground'>elegir el procedimiento adecuado</strong> para un tipo de producto, analizar las regulaciones y requisitos locales, seleccionar en qué países lanzar y asesorar sobre el tiempo hasta la comercialización.",
      "Varios componentes de una <strong class='text-foreground'>buena estrategia regulatoria</strong> se solapan con conceptos de comercialización y marketing. Las empresas deben <strong class='text-foreground'>integrar la estrategia regulatoria en sus estrategias generales de desarrollo, negocio y operaciones</strong>. No invertir desde el principio suele generar repercusiones negativas más adelante.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "En QbD Group, entendemos las complejidades de navegar el desarrollo de fármacos y las rutas regulatorias. Nuestra orientación experta asegura que tus proyectos estén estratégicamente alineados desde el principio.",
    whyBenefits: [
      { title: "Acertar a la primera", text: "Formula la estrategia correcta desde el inicio." },
      { title: "Aportación regulatoria", text: "Asegura la aportación regulatoria durante tus programas de desarrollo de fármacos." },
      { title: "Reducir el time-to-market", text: "Elige la estrategia correcta para reducir el tiempo hasta la comercialización." },
      { title: "Alineación con el negocio", text: "Garantiza la compatibilidad con las expectativas empresariales." },
      { title: "Cumplir plazos", text: "Cumple con los plazos de presentación." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs. Con más de una década de experiencia, ofrecemos soporte personalizado a lo largo de todo el ciclo de vida del producto.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Drug Development ───────── */
  "drug-development": {
    title: "Desarrollo de fármacos",
    subtitle: "Del descubrimiento al mercado",
    description: "El desarrollo de fármacos incluye el descubrimiento, las pruebas preclínicas y clínicas. Las nuevas entidades químicas deben demostrar estándares mínimos de seguridad, toxicidad, farmacocinética y metabolismo antes de poder comercializarse.",
    whatTitle: "¿Qué es el desarrollo de fármacos?",
    whatParagraphs: [
      "<strong class='text-foreground'>Del descubrimiento al mercado: Descubrimiento ⟶ Preclínica ⟶ Ensayos clínicos</strong>",
      "El desarrollo de fármacos incluye el descubrimiento, las pruebas preclínicas y clínicas. Las nuevas entidades químicas (NCE) deben demostrar estándares mínimos de seguridad, toxicidad, farmacocinética y metabolismo en animales (pruebas preclínicas) y humanos (ensayos clínicos) y <strong class='text-foreground'>fabricarse según altos estándares de calidad</strong> antes de poder comercializarse.",
      "El proceso de desarrollo de fármacos es muy largo (hasta 10 años) y costoso. Solo unas pocas NCE superan todas las pruebas y llegan al mercado.",
      "Para garantizar la comercialización, los programas de desarrollo de fármacos deben <strong class='text-foreground'>satisfacer los requisitos regulatorios</strong> de las autoridades de licencia. La mejor manera de asegurarlo es formular una estrategia regulatoria sólida en una fase temprana del proceso.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Llevar un producto farmacéutico al mercado requiere experiencia para garantizar el cumplimiento, optimizar los procesos y lograr resultados exitosos.",
    whyBenefits: [
      { title: "Cumplimiento regulatorio", text: "Te guiamos para cumplir todos los requisitos regulatorios, asegurando que tu producto cumpla con los estándares de las autoridades de licencia." },
      { title: "Optimización de pruebas preclínicas y clínicas", text: "Nos aseguramos de que tus pruebas sean exhaustivas y apropiadas, adaptadas a las necesidades específicas de tu producto." },
      { title: "Estrategias de prueba personalizadas", text: "Te ayudamos a diferenciar y abordar los requisitos de prueba para diversos tipos de productos, incluyendo NCE, extensiones de línea, genéricos y nuevas indicaciones." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Elegir QbD significa asociarte con un líder de confianza en Regulatory Affairs. Con más de una década de experiencia, ofrecemos soporte personalizado a lo largo de todo el ciclo de vida del producto.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Early Development ───────── */
  "early-development": {
    title: "Desarrollo en fase temprana",
    subtitle: "Navegando las primeras etapas del desarrollo de fármacos",
    description: "Un paso esencial en las primeras etapas del desarrollo de un medicamento potencial es la preparación de la solicitud de ensayo clínico para el primer estudio en humanos.",
    whatTitle: "¿Qué es el desarrollo en fase temprana?",
    whatParagraphs: [
      "Un paso esencial en las primeras etapas del desarrollo de un medicamento potencial es la preparación de la solicitud de ensayo clínico para el primer estudio en humanos (FIH).",
      "Los expertos de QbD Group te guiarán a través de este proceso y los pasos subsiguientes, por ejemplo asegurando el cumplimiento de los requisitos de la regulación pediátrica de la UE, aprovechando las oportunidades para el estatus de medicamento huérfano y buscando activamente la aportación de las autoridades regulatorias a través de procedimientos de asesoramiento científico.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Proporcionamos asistencia fluida en la preparación de documentación esencial y la navegación de requisitos regulatorios para tu desarrollo clínico.",
    whyBenefits: [
      { title: "Solicitud de ensayo clínico FIH", text: "Asistimos en la preparación y presentación de solicitudes de ensayos FIH, asegurando el cumplimiento de los requisitos regulatorios." },
      { title: "Preparación de documentación", text: "Nuestro equipo garantiza la exactitud, integridad y cumplimiento de toda la documentación necesaria, incluyendo CTA e IMPD." },
      { title: "Medicamentos huérfanos y planes pediátricos", text: "Apoyamos los esfuerzos para obtener la designación de medicamento huérfano y desarrollar planes de investigación pediátrica." },
      { title: "Solicitudes PRIME y asesoramiento científico", text: "Ofrecemos orientación experta para solicitudes PRIME y asesoramiento científico." },
    ],
    helpCards: {
      heading: "Estrategias de desarrollo temprano",
      intro: "Navegar las primeras etapas del desarrollo de fármacos es fundamental para establecer una base sólida para el éxito.",
      cards: [
        { title: "Desarrollo en fase inicial", text: "Soporte integral para las fases más tempranas de tu programa de desarrollo de fármacos." },
        { title: "Solicitudes de ensayo clínico", text: "Preparación y presentación de CTA, protocolo, IB e IMPD." },
        { title: "Designaciones de medicamentos huérfanos", text: "Soporte para obtener la designación de medicamento huérfano (ODD)." },
        { title: "Planes de investigación pediátrica", text: "Cumplimiento de los requisitos de la regulación pediátrica de la UE." },
      ],
    },
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD significa asociarte con un líder de confianza en Regulatory Affairs.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Scientific Advice ───────── */
  "scientific-advice": {
    title: "Asesoramiento científico",
    subtitle: "Orientación experta para interacciones con autoridades regulatorias",
    description: "Los procedimientos de asesoramiento científico desempeñan un papel central en el proceso de aprobación de fármacos. Nuestros consultores experimentados te ayudan a interactuar con todas las autoridades sanitarias relevantes.",
    whatTitle: "Tipos de asesoramiento científico",
    whatParagraphs: [
      "Un paso esencial en las primeras etapas del desarrollo de un medicamento potencial es la preparación de la solicitud de ensayo clínico para el primer estudio en humanos (FIH).",
      "Los expertos de QbD Group te guiarán a través de este proceso y los pasos subsiguientes, asegurando el cumplimiento de los requisitos de la regulación pediátrica de la UE, aprovechando las oportunidades para el estatus de medicamento huérfano y buscando activamente la aportación de las autoridades regulatorias.",
    ],
    extraSections: [{
      title: "¿Qué implica?",
      paragraphs: [
        "<strong class='text-foreground'>Interacciones con agencias</strong> — Presenta con éxito tu solicitud de aprobación de fármacos ante las autoridades competentes.",
        "<strong class='text-foreground'>Preparación y comunicación</strong> — Las interacciones con las agencias desempeñan un papel central en el proceso de aprobación de fármacos. Según el procedimiento, nuestro equipo de consultores experimentados te ayuda a interactuar con todas las autoridades sanitarias relevantes.",
      ],
    }],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Ofrecemos una gama de servicios especializados para apoyar tus interacciones con las autoridades regulatorias.",
    whyBenefits: [
      { title: "Iniciar contacto con autoridades", text: "Asistimos en el establecimiento del contacto inicial con las autoridades regulatorias." },
      { title: "Preparar briefing packages", text: "Nuestro equipo prepara briefing packages completos para apoyar las presentaciones regulatorias." },
      { title: "Moderar reuniones con autoridades", text: "Gestionamos y moderamos eficientemente las reuniones con las autoridades regulatorias." },
      { title: "Orientación en dossier (CMC)", text: "Proporcionamos orientación experta en dossiers de Química, Fabricación y Controles (CMC)." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD significa asociarte con un líder de confianza en Regulatory Affairs.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Regulatory Procedures ───────── */
  "regulatory-procedures": {
    title: "Procedimientos regulatorios",
    subtitle: "Navegando las rutas de registro en la UE y el Reino Unido",
    description: "Existen varios procedimientos disponibles para registrar medicamentos en la UE y el Reino Unido. El método elegido depende del tipo de fármaco y la ruta prevista hacia el mercado.",
    whatTitle: "Procedimientos regulatorios en la UE y el Reino Unido",
    whatParagraphs: [
      "Existen varios procedimientos disponibles para registrar medicamentos en la UE y el Reino Unido. El <strong class='text-foreground'>método elegido depende del tipo de fármaco</strong> (venta libre – OTC, medicamentos con receta – Rx, genéricos, nuevas entidades químicas, productos derivados de biotecnología, …) y la ruta prevista hacia el mercado.",
      "<strong class='text-foreground'>La legislación europea establece ciertos procedimientos para tipos específicos de productos</strong>, por ejemplo el procedimiento centralizado es obligatorio para productos derivados de biotecnología, medicamentos huérfanos y fármacos diseñados para ciertos grupos de enfermedades como SIDA, cáncer, trastornos neurodegenerativos y diabetes.",
      "Nuestro equipo en el Reino Unido también puede apoyarte con solicitudes nacionales UK/GB y el procedimiento de reconocimiento internacional.",
      "Por último, nuestros expertos también pueden apoyarte para llevar tus fármacos al mercado estadounidense.",
    ],
    helpCards: {
      heading: "Tipos de procedimientos",
      intro: "Te guiamos a través de cada ruta de registro disponible en la UE y el Reino Unido:",
      cards: [
        { title: "Procedimiento nacional", text: "Si deseas obtener una licencia en un único Estado miembro, la solicitud se presenta ante la autoridad competente nacional." },
        { title: "Reconocimiento mutuo (MRP)", text: "Para productos a registrar en más de un Estado miembro. Basado en una licencia nacional aprobada en un Estado miembro (RMS) siendo mutuamente reconocida en otros países de la UE (CMS)." },
        { title: "Procedimiento descentralizado (DCP)", text: "Sigue los mismos principios del MRP. Se utiliza cuando el producto aún no ha recibido una autorización de comercialización en ningún Estado miembro." },
        { title: "Procedimiento centralizado (CP)", text: "Solicitud a la EMA para una autorización comunitaria válida en toda la UE. Obligatorio para productos biotecnológicos, medicamentos huérfanos y productos para SIDA, cáncer, trastornos neurodegenerativos y diabetes." },
      ],
    },
    extraSections: [
      {
        title: "Extensiones de línea",
        paragraphs: [
          "El Reglamento (CE) n.º 1084/2003 (MRP o DCP) y el Reglamento (CE) n.º 1085/2003 (para CP) describen en el anexo II los tipos de solicitudes que quedan fuera del alcance de las \"variaciones\" pero al mismo tiempo no son solicitudes completamente nuevas.",
        ],
      },
      {
        title: "Solicitudes nacionales UK/GB y Procedimiento de Reconocimiento Internacional (IRP)",
        paragraphs: [
          "Los solicitantes pueden solicitar la autorización de comercialización en el Reino Unido mediante una solicitud nacional o utilizando el IRP para medicamentos ya autorizados por uno de los países/regiones de referencia especificados por la MHRA.",
        ],
      },
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Navegar el panorama regulatorio puede ser complejo, pero con nuestra orientación experta, puedes avanzar con confianza.",
    whyBenefits: [
      { title: "Elegir el procedimiento adecuado", text: "Te ayudamos a seleccionar la ruta de registro correcta para tu tipo de producto y estrategia de mercado." },
      { title: "Asesorar en estrategia regulatoria", text: "Planificación regulatoria estratégica alineada con tus objetivos de negocio y cartera de productos." },
      { title: "Contactar con autoridades", text: "Establecemos y gestionamos la comunicación con las autoridades competentes en tu nombre." },
      { title: "Organizar y liderar reuniones", text: "Coordinamos y lideramos reuniones con autoridades regulatorias para asegurar resultados productivos." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Submissions ───────── */
  "submissions": {
    title: "Presentaciones regulatorias",
    subtitle: "Consiguiendo tu autorización de comercialización a la primera",
    description: "Navegar el panorama regulatorio para la autorización de comercialización puede ser complejo, con requisitos estrictos de documentación y cumplimiento. Nos aseguramos de que tus solicitudes cumplan todos los estándares regulatorios.",
    whatTitle: "Tipos de solicitudes en la UE",
    whatParagraphs: [
      "Navegar el panorama regulatorio para la autorización de comercialización puede ser complejo, con requisitos estrictos de documentación y cumplimiento. Ya sea que prepares una solicitud completa para un producto original, una solicitud abreviada para un genérico o un dossier para un producto combinado fijo, la precisión es clave.",
      "En QbD Group, nos especializamos en ayudarte a realizar tus presentaciones correctamente a la primera. Con nuestra experiencia, nos aseguramos de que tus solicitudes cumplan todos los estándares regulatorios.",
    ],
    helpCards: {
      heading: "Tipos de solicitudes",
      intro: "Explora los tipos de solicitudes en la UE y cómo apoyamos cada una:",
      cards: [
        { title: "Solicitudes completas", text: "Para solicitudes completas, la autorización de comercialización debe ir acompañada de los datos y documentos establecidos en el artículo 8 de la directiva 2001/83/CE." },
        { title: "Solicitudes abreviadas (genéricos)", text: "Ya sea con consentimiento informado del originador o esencialmente similar a un producto autorizado durante 6 a 10 años." },
        { title: "Productos de combinación fija", text: "Cualquier combinación fija es un medicamento nuevo y único que requiere una autorización de comercialización separada y un RCP." },
        { title: "Solicitudes bibliográficas", text: "Soporte para solicitudes de uso bien establecido." },
      ],
    },
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Tus solicitudes de autorización de comercialización deben ir acompañadas correctamente por la documentación requerida.",
    whyBenefits: [
      { title: "Asesoramiento en contenido y estructura del dossier", text: "Orientación experta en contenido y estructura del dossier alineada con las expectativas regulatorias." },
      { title: "Análisis de brechas", text: "Análisis exhaustivo de brechas para identificar y abordar deficiencias documentales antes de la presentación." },
      { title: "Compilación de dossiers técnicos", text: "Compilación completa de dossiers técnicos que cumplen todos los requisitos regulatorios." },
      { title: "Paquetes de solicitud listos para la presentación", text: "Paquetes de solicitud completos listos para su presentación ante las autoridades de la UE." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── eCTD Publishing ───────── */
  "ectd-publishing": {
    title: "Publicación eCTD",
    subtitle: "Presentaciones electrónicas hechas correctamente",
    description: "Basado en el formato del Documento Técnico Común, el eCTD transfiere información regulatoria de la industria a la agencia en formato electrónico.",
    whatTitle: "El Documento Técnico Común electrónico (eCTD)",
    whatParagraphs: [
      "Basado en el contenido del formato del Documento Técnico Común (CTD), el Documento Técnico Común electrónico (eCTD) <strong class='text-foreground'>transfiere información regulatoria de la industria a la agencia en formato electrónico</strong>.",
      "<strong class='text-foreground'>Todos los tipos de información regulatoria pueden presentarse en formato electrónico</strong>, incluyendo nuevas solicitudes, variaciones y renovaciones para procedimientos nacionales, de reconocimiento mutuo, descentralizados y centralizados.",
    ],
    extraSections: [{
      title: "Cómo crear eCTD",
      paragraphs: [
        "Los archivos en un eCTD deben organizarse según la versión 3.2 de las especificaciones ICH eCTD y la versión actual de las especificaciones del Módulo 1 de la UE.",
        "Para utilizar eCTD, los titulares de autorización de comercialización deben invertir en <strong class='text-foreground'>software apropiado</strong> y mantener sistemas de soporte.",
      ],
    }],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Gestionar las presentaciones eCTD requiere precisión, experiencia y las herramientas adecuadas. En QbD Group, proporcionamos soporte integral.",
    whyBenefits: [
      { title: "Validar y seleccionar software eCTD", text: "Te ayudamos a evaluar, validar y seleccionar el software eCTD más adecuado." },
      { title: "Compilar eCTD precisos y conformes", text: "Compilación y publicación completa de presentaciones eCTD para todos los tipos de procedimiento." },
      { title: "Preparar y presentar solicitudes electrónicas", text: "Preparación y presentación de extremo a extremo de solicitudes electrónicas ante las autoridades regulatorias." },
      { title: "Mantenimiento y soporte continuo de eCTD", text: "Soporte continuo del ciclo de vida del eCTD incluyendo variaciones, renovaciones y validación técnica." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Artwork & Labeling ───────── */
  "artwork-labeling": {
    title: "Packaging y etiquetado",
    subtitle: "Marketing en cumplimiento",
    description: "La Directiva 2001/83/CE exige que los textos de las etiquetas estén en conformidad con la Ficha Técnica aprobada. QbD Group te ayuda a crear artwork farmacéutico en pleno cumplimiento.",
    whatTitle: "Marketing en cumplimiento",
    whatParagraphs: [
      "<strong class='text-foreground'>La Directiva 2001/83/CE exige que los textos de las etiquetas estén en conformidad con la Ficha Técnica (SmPC) aprobada.</strong>",
      "Para productos registrados de forma centralizada existe un único SmPC; para procedimientos de reconocimiento mutuo o descentralizados, los SmPC están armonizados para los países donde el producto está registrado.",
      "Para licencias nacionales \"antiguas\" con SmPC posiblemente diferentes, los textos de las etiquetas y el prospecto pueden diferir de un país a otro.",
    ],
    extraSections: [{
      title: "Requisitos de software",
      paragraphs: [
        "<strong class='text-foreground'>Software de gestión documental + procesos de control de cambios:</strong>",
        "• Archivado apropiado y gestión electrónica de documentos para materiales de envasado<br/>• Números de revisión<br/>• Control de cambios<br/>• Gestión de flujos de proceso<br/>• Versionado<br/>• SOPs<br/>• Provisión de herramientas de autoría basadas en XML",
      ],
    }],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "La información de producto precisa, conforme y fácil de usar es esencial para cumplir con los estándares regulatorios y garantizar la seguridad del paciente.",
    whyBenefits: [
      { title: "Preparar PIL y textos de etiquetas conformes", text: "Creación y revisión de PIL y textos de etiquetas alineados con los SmPC aprobados y requisitos regulatorios." },
      { title: "Supervisar proyectos de traducción", text: "Coordinación y supervisión de proyectos de traducción en todos los idiomas requeridos para tus mercados." },
      { title: "Revisar PIL y textos para cumplimiento", text: "Revisión exhaustiva asegurando la conformidad con los requisitos de la Directiva 2001/83/CE." },
      { title: "Asesoramiento experto en user testing para PIL", text: "Orientación especializada en metodologías de user testing para asegurar la comprensión del paciente." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Lifecycle Management ───────── */
  "lifecycle-management": {
    title: "Gestión del ciclo de vida",
    subtitle: "Gestión de cambios a lo largo del ciclo de vida del producto",
    description: "Los productos que han sido comercializados cambian con el tiempo y este proceso debe gestionarse. Los expertos de QbD Group te acompañan durante todo el proceso de gestión del ciclo de vida.",
    whatTitle: "Reaccionar a los cambios",
    whatParagraphs: [
      "Los productos que han sido comercializados <strong class='text-foreground'>cambian con el tiempo</strong> y este <strong class='text-foreground'>proceso debe gestionarse</strong>, a medida que avanza a través de sus sucesivas etapas.",
      "En la industria farmacéutica, existe una guía clara sobre los tipos de variaciones y requisitos del dossier.",
      "Para productos registrados vía MRP o DCP, cualquier variación debe presentarse simultáneamente ante las autoridades competentes de los Estados miembros donde el producto está en el mercado.",
      "El uso de soluciones de software de control de cambios puede facilitar el control efectivo de todos los cambios post-registro.",
      "Lograr un precio basado en el valor es un reto para lanzar tu producto en España. QbD ofrece soporte para colocar tu producto en el mercado según la estrategia regulatoria y cumpliendo los requisitos nacionales, incluyendo precio y reembolso.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Adaptarse a los cambios regulatorios puede ser complejo. En QbD Group, proporcionamos soluciones a medida para asegurar que tus presentaciones cumplan los más altos estándares.",
    whyBenefits: [
      { title: "Preparar variaciones", text: "Asegurar que toda la documentación requerida sea precisa y completa para las presentaciones de variaciones." },
      { title: "Presentar ante autoridades", text: "Gestionar y presentar eficientemente las solicitudes de variación ante las autoridades competentes." },
      { title: "Implementar control de cambios", text: "Establecer y mantener un sistema robusto para el seguimiento y gestión de cambios." },
      { title: "Formar a tus equipos", text: "Proporcionar formación práctica a tus profesionales sobre procesos regulatorios." },
      { title: "Asesorar en software", text: "Recomendar e implementar las herramientas adecuadas para la gestión de tareas regulatorias." },
      { title: "Gestionar serialización", text: "Gestionar los códigos nacionales y requisitos de serialización de forma fluida." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Information Officer ───────── */
  "information-officer": {
    title: "Responsable de información",
    subtitle: "Gestión de la información farmacéutica y el cumplimiento del marketing",
    description: "Para cumplir plenamente con los requisitos legales, un experto cualificado debe asumir la responsabilidad sobre cómo se comunica y comercializa la información sobre los medicamentos.",
    whatTitle: "Gestionando tu información",
    whatParagraphs: [
      "<strong class='text-foreground'>¿Hasta dónde pueden llegar las empresas farmacéuticas al publicitar sus productos? ¿Qué información debe comunicarse en la Ficha Técnica?</strong>",
      "Para cumplir plenamente con los requisitos legales, un experto cualificado debe asumir la responsabilidad sobre cómo se comunica y comercializa la información sobre los medicamentos.",
      "<strong class='text-foreground'>Responsabilidades del responsable de información:</strong>",
      "• Debe tener un <strong class='text-foreground'>sólido conocimiento del campo médico y científico</strong> para aplicar todas las directrices relevantes de manera adecuada.<br/>• Es <strong class='text-foreground'>responsable</strong> de asegurar que el etiquetado, prospectos, información de producto y publicidad se alineen con su aprobación o registro.<br/>• Debe <strong class='text-foreground'>mantenerse informado</strong> sobre los cambios relevantes en regulaciones, leyes y plazos e implementarlos en consecuencia.<br/>• <strong class='text-foreground'>Coordina</strong> con las autoridades relevantes según sea necesario.<br/>• <strong class='text-foreground'>Realiza sesiones de formación</strong> para concienciar al personal de ventas y marketing sobre las regulaciones legales aplicables.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Las empresas farmacéuticas operan dentro de marcos legales estrictos en lo que respecta a la comunicación y comercialización de sus productos.",
    whyBenefits: [
      { title: "Experiencia en la industria", text: "Nuestro equipo aporta años de experiencia en los sectores farmacéutico y de Life Sciences." },
      { title: "Soluciones integrales", text: "Nos aseguramos de que todos tus materiales cumplan los requisitos legales, incluyendo etiquetado, PIL, SmPC y materiales publicitarios." },
      { title: "Empoderar a tu equipo", text: "A través de sesiones de formación específicas, equipamos a tus equipos de ventas y marketing con el conocimiento para navegar los límites legales." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Orphan Diseases ───────── */
  "orphan-diseases": {
    title: "Enfermedades raras",
    subtitle: "Apoyando el desarrollo de medicamentos para enfermedades raras",
    description: "Las enfermedades huérfanas son condiciones que afectan a un pequeño porcentaje de la población. QbD Group puede apoyarte en el desarrollo de medicamentos para pacientes que padecen enfermedades raras.",
    whatTitle: "¿Qué son las enfermedades huérfanas?",
    whatParagraphs: [
      "Las enfermedades huérfanas, también conocidas como <strong class='text-foreground'>enfermedades raras</strong>, son <strong class='text-foreground'>condiciones que afectan a un pequeño porcentaje de la población</strong>.",
      "A pesar de su rareza, existen más de 7.000 enfermedades huérfanas identificadas, que afectan colectivamente a millones de personas en todo el mundo. <strong class='text-foreground'>Estas condiciones a menudo carecen de tratamientos efectivos debido al limitado interés comercial</strong>, lo que hace vital el desarrollo de medicamentos huérfanos para mejorar la calidad de vida de los pacientes.",
      "Para fomentar la innovación en este ámbito, agencias regulatorias como la EMA ofrecen la designación de medicamento huérfano (ODD), proporcionando a las empresas farmacéuticas incentivos como:",
      "• Exclusividad de mercado extendida<br/>• Tasas regulatorias reducidas<br/>• Orientación científica especializada mediante asistencia de protocolo",
      "QbD Group está aquí para ayudarte a navegar las complejidades del desarrollo de medicamentos huérfanos.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "En QbD Group, ofrecemos soporte de principio a fin para solicitudes de designación de medicamento huérfano (ODD) y cumplimiento continuo.",
    whyBenefits: [
      { title: "Soporte experto en solicitudes ODD", text: "Asistimos en cada etapa del proceso de solicitud ODD, incluyendo preparación de presentaciones y reuniones pre-presentación." },
      { title: "Cumplimiento más allá de la designación", text: "Nuestro equipo asegura que cumplas todas las obligaciones post-designación, como informes anuales y mantenimiento del estatus ODD." },
      { title: "Orientación personalizada", text: "Con nuestra profunda experiencia regulatoria, proporcionamos estrategias personalizadas para maximizar incentivos." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Elegir QbD Group significa asociarte con un líder de confianza en Regulatory Affairs con más de una década de experiencia.",
    partnerBullets: [
      "+10 años de experiencia: benefíciate de nuestro equipo de consultores altamente cualificados.",
      "Soporte durante todo el ciclo de vida: apoyo completo desde la fase pre- hasta la post-comercialización.",
      "Presencia global: QbD Group opera a nivel global, con un enfoque especial en Europa (UE) y China.",
      "Satisfacción del cliente: evaluación constante, transparencia y objetivos claros.",
    ],
  },

  /* ───────── Regulatory Intelligence ───────── */
  "regulatory-intelligence": {
    title: "Inteligencia regulatoria",
    subtitle: "Mantente a la vanguardia del panorama regulatorio",
    description: "La inteligencia regulatoria es esencial para un desarrollo exitoso de dispositivos y una estrategia regulatoria efectiva. Aprovechando información actualizada y completa, puedes mantenerte por delante de la competencia.",
    whatTitle: "Lo que ofrecemos",
    whatParagraphs: [
      "La inteligencia regulatoria es esencial para un desarrollo exitoso de dispositivos y una estrategia regulatoria efectiva. Aprovechando información actualizada y completa, no solo puedes <strong class='text-foreground'>mantenerte por delante de la competencia</strong> sino también asegurar que tus productos cumplan plenamente con las regulaciones actuales y en constante evolución.",
      "<strong class='text-foreground'>La inteligencia regulatoria puede incluir:</strong>",
      "• Información regulatoria<br/>• Información de la competencia<br/>• Información sobre requisitos legales<br/>• Actualizaciones legislativas",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "QbD Group proporciona inteligencia regulatoria integral adaptada a las necesidades cambiantes de los fabricantes de Medical Devices.",
    whyBenefits: [
      { title: "Información regulatoria", text: "Monitorizamos y analizamos las actualizaciones regulatorias, asegurando que recibas información oportuna." },
      { title: "Determinar requisitos legales", text: "Nuestros expertos ayudan a interpretar regulaciones complejas, guiándote en la alineación con todos los estándares legales aplicables." },
      { title: "Soporte legislativo a medida", text: "Asesoramos sobre actualizaciones legislativas específicas y aseguramos que te mantengas a la vanguardia de cualquier cambio." },
      { title: "Gestión de la evolución de estándares", text: "Hacemos seguimiento de estándares nuevos y obsoletos, ayudándote a adaptarte a los últimos requisitos." },
      { title: "Paquetes de información completos", text: "Nuestros paquetes de información detallados ofrecen un análisis exhaustivo de cualquier consulta regulatoria." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "QbD Group ofrece inteligencia regulatoria a medida para mantenerte informado y conforme.",
    partnerBullets: [
      "Equipo dedicado de expertos regulatorios con profundo conocimiento de la industria.",
      "Monitorización proactiva de cambios regulatorios en mercados clave.",
      "Informes personalizados adaptados a tu cartera de productos específica.",
      "Orientación estratégica que combina inteligencia regulatoria con planificación de negocio.",
    ],
  },

  /* ───────── Regulatory Strategy for MD & IVD ───────── */
  "regulatory-strategy-md-ivd": {
    title: "Estrategia regulatoria para MD e IVD",
    subtitle: "Un enfoque estratégico para el acceso al mercado de Medical Devices e IVD",
    description: "Una estrategia regulatoria es un marco esencial que delinea los pasos y acciones necesarios para abordar eficazmente los requisitos regulatorios para introducir un dispositivo médico o IVD en el mercado.",
    whatTitle: "¿Qué es una estrategia regulatoria para MD e IVD?",
    whatParagraphs: [
      "Una estrategia regulatoria es un marco esencial que delinea los pasos y acciones necesarios para abordar eficazmente los requisitos regulatorios para introducir un <strong class='text-foreground'>dispositivo médico o IVD en el mercado</strong>.",
      "Este enfoque estratégico permite a los fabricantes navegar eficientemente por las complejidades del panorama regulatorio, asegurando el cumplimiento y facilitando la entrada oportuna al mercado.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "En QbD Group, te ayudamos a desarrollar una estrategia regulatoria sólida para tu dispositivo médico o IVD.",
    whyBenefits: [
      { title: "Clasificación del dispositivo", text: "Determinamos la clasificación correcta de tu dispositivo según MDR o IVDR." },
      { title: "Hoja de ruta regulatoria", text: "Creamos una hoja de ruta regulatoria clara alineada con tus objetivos de negocio." },
      { title: "Selección de organismo notificado", text: "Te guiamos en la selección del organismo notificado adecuado." },
      { title: "Planificación de la documentación", text: "Planificamos la documentación técnica necesaria para la conformidad." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "QbD Group combina profunda experiencia regulatoria con conocimiento práctico de la industria MedTech.",
    partnerBullets: [
      "Décadas de experiencia en estrategia regulatoria para MD e IVD.",
      "Experiencia directa con los principales organismos notificados europeos.",
      "Enfoque pragmático alineado con tus objetivos comerciales.",
      "Soporte integral desde la clasificación hasta la certificación.",
    ],
  },

  /* ───────── Design & Development ───────── */
  "design-and-development": {
    title: "Diseño y desarrollo",
    subtitle: "Diseñando Medical Devices seguros y efectivos desde el concepto hasta el mercado",
    description: "El proceso de diseño y desarrollo es un elemento central para llevar Medical Devices e IVD seguros y efectivos al mercado. Nuestro equipo proporciona orientación experta a lo largo de todo el ciclo de diseño.",
    whatTitle: "¿Qué es el diseño y desarrollo de Medical Devices?",
    whatParagraphs: [
      "El proceso de diseño y desarrollo es un elemento central para llevar <strong class='text-foreground'>Medical Devices e IVD seguros y efectivos al mercado</strong>.",
      "Nuestro equipo proporciona orientación experta a lo largo de todo el ciclo de diseño, asegurando el cumplimiento de los requisitos regulatorios desde el concepto inicial.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Ofrecemos soporte integral durante todo el proceso de diseño y desarrollo de tu dispositivo.",
    whyBenefits: [
      { title: "Planificación del diseño", text: "Desarrollo de planes de diseño conformes con ISO 13485 e IEC 62304." },
      { title: "Gestión de riesgos", text: "Análisis de riesgos según ISO 14971 integrado en el proceso de diseño." },
      { title: "Verificación y validación", text: "Planificación y ejecución de actividades de verificación y validación del diseño." },
      { title: "Documentación técnica", text: "Compilación de documentación técnica conforme con MDR/IVDR." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "QbD Group aporta décadas de experiencia en diseño y desarrollo de Medical Devices.",
    partnerBullets: [
      "Experiencia profunda en ISO 13485, ISO 14971 e IEC 62304.",
      "Enfoque integrado que combina calidad, regulatorio y diseño.",
      "Soporte desde el concepto hasta la documentación lista para certificación.",
      "Experiencia directa con las expectativas de los organismos notificados.",
    ],
  },

  /* ───────── Technical Documentation ───────── */
  "technical-documentation": {
    title: "Documentación técnica",
    subtitle: "Documentación regulatoria integral para MD e IVD",
    description: "Las nuevas regulaciones para IVD y Medical Devices introducen requisitos más estrictos para la documentación técnica y la evidencia clínica. Con más de dos décadas de experiencia, ofrecemos una experiencia inigualable.",
    whatTitle: "Documentación técnica para MD e IVD",
    whatParagraphs: [
      "Las nuevas regulaciones para IVD y Medical Devices introducen <strong class='text-foreground'>requisitos más estrictos</strong> para la documentación técnica y la evidencia clínica.",
      "Con más de dos décadas de experiencia, ofrecemos una experiencia inigualable en la compilación de documentación técnica conforme con MDR e IVDR.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Proporcionamos soporte integral en documentación técnica para Medical Devices e IVD.",
    whyBenefits: [
      { title: "Compilación STED", text: "Compilación completa de Summary Technical Documentation (STED) para presentaciones a organismos notificados." },
      { title: "Análisis de brechas", text: "Evaluación exhaustiva de la documentación existente frente a los requisitos MDR/IVDR." },
      { title: "Descripción del dispositivo", text: "Documentación detallada del dispositivo, incluyendo principio de funcionamiento y especificaciones técnicas." },
      { title: "Evaluación biológica", text: "Soporte en evaluación de biocompatibilidad según ISO 10993." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "QbD Group es tu socio de confianza para documentación técnica conforme con MDR e IVDR.",
    partnerBullets: [
      "Más de 20 años de experiencia en documentación técnica para Medical Devices e IVD.",
      "Profundo conocimiento de los requisitos y expectativas de los organismos notificados.",
      "Enfoque integral que cubre todos los anexos del MDR/IVDR.",
      "Trayectoria demostrada en certificaciones CE exitosas.",
    ],
  },

  /* ───────── Notified Body Submissions ───────── */
  "notified-body-submissions": {
    title: "Presentaciones a organismos notificados",
    subtitle: "Tu camino hacia el marcado CE",
    description: "Navegar el proceso de presentación a un organismo notificado es un paso esencial para obtener el marcado CE para Medical Devices e IVD. Ofrecemos soporte integral adaptado a tus requisitos.",
    whatTitle: "¿Qué es una presentación a un organismo notificado?",
    whatParagraphs: [
      "Navegar el proceso de presentación a un organismo notificado es un paso esencial para obtener el <strong class='text-foreground'>marcado CE</strong> para Medical Devices e IVD.",
      "Ofrecemos soporte integral adaptado a tus requisitos, asegurando que tu documentación cumpla con todas las expectativas del organismo notificado.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Proporcionamos soporte experto para cada etapa del proceso de presentación a organismos notificados.",
    whyBenefits: [
      { title: "Preparación de la presentación", text: "Compilación y revisión completa de la documentación de presentación." },
      { title: "Orientación experta", text: "Nuestro equipo tiene un conocimiento profundo de los requisitos regulatorios y las expectativas de los organismos notificados." },
      { title: "Defensa eficiente", text: "Actuamos como tu enlace durante el proceso de evaluación, abordando consultas y resolviendo problemas." },
      { title: "Principio de acertar a la primera", text: "Asegurando precisión y exhaustividad en cada paso, minimizamos retrasos." },
      { title: "Relaciones sólidas con NB", text: "Nuestra relación con los organismos notificados facilita interacciones más rápidas y evaluaciones más fluidas." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "Nuestra profunda experiencia con presentaciones a organismos notificados asegura un camino fluido hacia el marcado CE.",
    partnerBullets: [
      "Trayectoria demostrada de presentaciones exitosas a NB en todas las clases de dispositivos.",
      "Experiencia directa con los principales organismos notificados europeos.",
      "Experiencia en requisitos de presentación tanto MDR como IVDR.",
      "Soporte de principio a fin desde la evaluación de brechas hasta la certificación.",
    ],
  },

  /* ───────── Post-Market Surveillance ───────── */
  "post-market-surveillance": {
    title: "Vigilancia poscomercialización",
    subtitle: "Garantizando la seguridad y el cumplimiento continuos",
    description: "La vigilancia poscomercialización es un requisito esencial para garantizar la seguridad y eficacia continuas de tu IVD o dispositivo médico una vez que llega al mercado.",
    whatTitle: "Soluciones integrales para fabricantes de MD e IVD",
    whatParagraphs: [
      "Nuestro equipo de expertos trabajará contigo para desarrollar una estrategia de PMS robusta y conforme con la UE, adaptada a las necesidades únicas de tu producto.",
      "También ofrecemos orientación sobre el <strong class='text-foreground'>proceso de control de cambios</strong>, asegurando que cualquier modificación a tu producto o documentación se gestione de manera eficiente y en pleno cumplimiento.",
    ],
    extraSections: [{
      title: "Notificación de vigilancia y formación",
      paragraphs: [
        "La vigilancia efectiva es una piedra angular de la seguridad poscomercialización. Apoyamos a los fabricantes con:",
        "• <strong class='text-foreground'>Notificación de incidentes graves</strong> y acciones correctivas de seguimiento<br/>• <strong class='text-foreground'>Establecimiento de un proceso de vigilancia</strong> personalizado para alinearse con tu sistema de Quality Assurance<br/>• <strong class='text-foreground'>Evaluación de posibles casos de vigilancia</strong> para determinar su notificabilidad<br/>• <strong class='text-foreground'>Formación integral en vigilancia</strong> para tu equipo",
      ],
    }],
    whyTitle: "Cómo podemos apoyar tus necesidades de PMS",
    whyIntro: "Nuestros servicios están diseñados para simplificar el cumplimiento y fortalecer el rendimiento del producto.",
    whyBenefits: [
      { title: "Evaluaciones de impacto", text: "Evalúa el impacto de los cambios en tus presentaciones regulatorias y determina las acciones de seguimiento requeridas." },
      { title: "Desarrollo de procesos", text: "Desarrollo de procesos para cumplir los estrictos requisitos del IVDR y MDR para actividades poscomercialización." },
      { title: "Seguimiento poscomercialización", text: "Realización de estudios, revisiones bibliográficas y más como parte de los requisitos de PMCF (MD) y PMPF (IVD)." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Con un profundo conocimiento de los marcos regulatorios y las mejores prácticas de la industria, ofrecemos orientación experta para asegurar que tus actividades de PMS sean conformes, efectivas e integradas en tus operaciones.",
    partnerBullets: [
      "Profunda experiencia en requisitos poscomercialización de MDR e IVDR.",
      "Implementación integral de sistemas de PMS y vigilancia.",
      "Enfoque proactivo en cumplimiento y gestión de riesgos.",
      "Formación y soporte para tu equipo interno de PMS.",
    ],
  },

  /* ───────── In-Country Representative Services ───────── */
  "in-country-representative-services": {
    title: "Servicios de representación en el país",
    subtitle: "Servicios de EU-REP, UKRP y CH-REP",
    description: "Nuestros expertos regulatorios actúan como EU-REP/UKRP/CH-REP para facilitar el acceso de dispositivos IVD y MD a los mercados europeo, del Reino Unido y suizo.",
    whatTitle: "¿Qué es un representante autorizado?",
    whatParagraphs: [
      "Un representante autorizado es una entidad legal establecida dentro de una jurisdicción específica que actúa en nombre de un <strong class='text-foreground'>fabricante no perteneciente a la UE / no perteneciente al Reino Unido / no perteneciente a Suiza</strong>. Sirve como punto de contacto principal entre el fabricante y las autoridades competentes locales.",
    ],
    helpCards: {
      heading: "Lo que ofrecemos",
      intro: "Nuestros expertos regulatorios actúan como EU-REP/UKRP/CH-REP para facilitar el acceso de tus dispositivos IVD y MD al mercado europeo/del Reino Unido/suizo.",
      cards: [
        { title: "Verificación de documentación", text: "Asegurar que la Declaración de Conformidad y los documentos técnicos han sido elaborados." },
        { title: "Confirmación de cumplimiento", text: "Verificar que el fabricante ha cumplido con las obligaciones de registro." },
        { title: "Etiquetado e identificación", text: "Confirmar que los dispositivos muestran el nombre y dirección del fabricante y del representante autorizado." },
        { title: "Gestión de incidentes y reclamaciones", text: "Servir como contacto principal para las autoridades de la UE/Reino Unido/Suiza." },
        { title: "Comunicación con autoridades", text: "Proporcionar la documentación requerida y mantener registros de cumplimiento." },
        { title: "Soporte de vigilancia del mercado", text: "Trabajar con las autoridades en acciones correctivas, vigilancia del mercado y retiradas de dispositivos." },
      ],
    },
    processTitle: "Más allá de la representación: tu socio integral de cumplimiento",
    processIntro: "Nuestro servicio de representante autorizado es parte de un <strong class='text-foreground'>marco regulatorio y de calidad completamente integrado</strong> que mantiene tu dispositivo conforme en cada etapa.",
    processSteps: [
      { title: "Soporte en Regulatory Affairs", text: "Soporte regulatorio estratégico que cubre el espectro completo de necesidades de cumplimiento." },
      { title: "Servicios de calidad y auditoría", text: "Auditorías internas, de proveedores o simulacros independientes para verificar tu QMS." },
      { title: "Vigilancia poscomercialización", text: "Soporte en la monitorización de obligaciones poscomercialización y asistencia en procesos de vigilancia." },
      { title: "Formación y revisiones de cumplimiento", text: "Acceso a profesionales experimentados en RA y QA para formación específica y asesoramiento estratégico." },
    ],
    whyTitle: "Asóciate con confianza: por qué elegir QbD Group como tu representante autorizado",
    whyIntro: "Proporcionamos representación experimentada, independiente y plenamente conforme en los principales mercados europeos.",
    whyBenefits: [
      { title: "Experiencia multimercado", text: "Aseguramos el pleno cumplimiento de las regulaciones MDR/IVDR de la UE, del Reino Unido y de Suiza." },
      { title: "Más que solo representación", text: "Más allá de cumplir los requisitos legales, ofrecemos soporte integral incluyendo estrategia regulatoria y documentación técnica." },
      { title: "Experiencia regulatoria transfronteriza", text: "Con una vasta red de consultores regulatorios en la UE, Reino Unido y Suiza." },
      { title: "Comunicación directa y experta", text: "Trabajarás directamente con profesionales regulatorios experimentados — rápido, transparente y personalizado." },
      { title: "Socio de acceso de confianza", text: "Como tu EU-REP, UKRP y CH-REP, servimos como punto de contacto oficial para las autoridades." },
      { title: "Servicios para todas las clases de dispositivos", text: "Proporcionamos servicios de representante autorizado consistentes y fiables en todas las clases de dispositivos." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "QbD Group es tu socio de confianza para servicios de representante autorizado en Europa.",
    partnerBullets: [
      "Visión global, experiencia local: con hubs regulatorios en toda Europa, proporcionamos soluciones inteligentes y escalables.",
      "Excelencia regulatoria: respaldados por líderes de la industria como Qarad, aportamos más de 20 años de experiencia.",
      "Soporte de principio a fin: desde el marcado CE hasta las obligaciones poscomercialización.",
      "Enfoque personalizado y proactivo: nuestro equipo trabaja estrechamente con el tuyo para alinear la estrategia regulatoria con tus objetivos de negocio.",
    ],
  },

  /* ───────── PRRC ───────── */
  "person-responsible-for-regulatory-compliance": {
    title: "Persona responsable del cumplimiento de la normativa (PRRC)",
    subtitle: "Requisito obligatorio del MDR e IVDR de la UE",
    description: "Para vender productos en el mercado de la UE, los fabricantes de Medical Devices e IVD deben designar oficialmente a una PRRC. Sin una PRRC designada oficialmente, las empresas no pueden comercializar sus productos en la UE.",
    whatTitle: "¿Qué es una PRRC?",
    whatParagraphs: [
      "Para vender productos en el mercado de la UE, los fabricantes de Medical Devices y/o IVD deben designar oficialmente una PRRC (Persona Responsable del Cumplimiento de la Normativa). Este rol es <strong class='text-foreground'>un requisito obligatorio bajo el MDR</strong> e <strong class='text-foreground'>IVDR</strong> de la UE.",
      "Sin una PRRC oficialmente designada, las empresas no pueden comercializar sus productos en la UE.",
      "Las pequeñas y micro empresas (menos de 50 empleados y facturación anual y/o balance <10M€) a menudo tienen dificultades para contratar recursos RAQS cualificados. Externalizar este servicio a nuestros expertos de QbD proporciona una solución eficiente.",
    ],
    extraSections: [
      {
        title: "Responsabilidades de una PRRC",
        paragraphs: [
          "La PRRC debe:",
          "• Asegurar el cumplimiento de las regulaciones de la UE<br/>• Revisar el QMS y la documentación técnica antes de que los productos lleguen al mercado<br/>• Ser notificada de auditorías, cambios en el QMS/producto, reclamaciones, retiradas o acciones correctivas de seguridad<br/>• Estar disponible de forma permanente y continua para la empresa",
          "<strong class='text-foreground'>Responsabilidad adicional para fabricantes de IVD</strong>",
          "Para los fabricantes de IVD, especialmente los que están fuera del EEE, la PRRC también debe asegurar que el representante autorizado de la UE cumpla con sus obligaciones.",
        ],
      },
    ],
    whyTitle: "¿Cómo podemos apoyarte?",
    whyIntro: "Proporcionamos servicios de PRRC personalizados según tus necesidades:",
    whyBenefits: [
      { title: "PRRC para Medical Devices", text: "Actuamos como tu PRRC para pequeñas o micro empresas. Consultamos sobre requisitos regulatorios para la designación y tareas de la PRRC." },
      { title: "PRRC para IVD", text: "Actuamos como tu PRRC para pequeñas o micro empresas. Actuamos como PRRC para el representante autorizado de la UE." },
      { title: "Consultoría regulatoria", text: "Consultoría experta sobre requisitos regulatorios para la designación de PRRC y tareas relacionadas." },
      { title: "Soporte en procedimientos", text: "Asistencia en la redacción o revisión de procedimientos relacionados con las responsabilidades de la PRRC." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Para asegurar un fácil acceso al mercado europeo, es importante elegir una persona responsable del cumplimiento regulatorio experimentada que pueda guiarte a través del proceso.",
    partnerBullets: [
      "Los expertos de QbD Group pueden actuar como tu PRRC externalizada, ayudando a tu empresa a cumplir con las regulaciones de la UE.",
      "Con más de 20 años de experiencia en la industria, podemos garantizarte el beneficio de un centro de excelencia externalizado de forma rentable.",
      "El proceso para designar una PRRC de QbD Group está bien estructurado y se basa en la participación intensiva de la persona designada.",
    ],
  },

  /* ───────── EU-REP ───────── */
  "authorized-representative-eu-rep": {
    title: "Representante autorizado (EU-REP)",
    subtitle: "Tu socio regulatorio en la UE",
    description: "Si eres un fabricante de Medical Devices o IVD fuera de la UE/EEE, designar un representante autorizado en la UE es un requisito legal para asegurar el cumplimiento del MDR e IVDR.",
    whatTitle: "¿Qué es un EU-REP y quién lo necesita?",
    whatParagraphs: [
      "Un EU-REP es una persona física o jurídica establecida en la Unión Europea que ha recibido y aceptado un mandato escrito de un fabricante situado fuera de la UE.",
      "Si eres un fabricante de Medical Devices o IVD fuera de la UE/EEE, <strong class='text-foreground'>designar un representante autorizado en la UE es un requisito legal</strong> para asegurar el cumplimiento del MDR y el IVDR.",
      "<strong class='text-foreground'>¿Quién necesita un EU-REP?</strong>",
      "• Fabricantes con sede fuera del Espacio Económico Europeo (EEE)<br/>• Empresas que comercializan dispositivos en el mercado de la UE bajo su propio nombre<br/>• Startups y PYMEs que se expanden desde EE.UU., Reino Unido o Asia a Europa",
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte como EU-REP?",
      intro: "Con sede en Bélgica y oficinas en toda la UE, QbD Group actúa como hub central para las comunicaciones regulatorias con las autoridades competentes europeas.",
      cards: [
        { title: "Representarte ante las autoridades competentes de la UE", text: "Representación oficial y comunicación con todos los organismos regulatorios relevantes de la UE." },
        { title: "Verificar documentación", text: "Verificar tu Declaración de Conformidad de la UE y que la documentación técnica ha sido elaborada." },
        { title: "Asegurar el cumplimiento del registro", text: "Verificar que el fabricante ha cumplido con las obligaciones de registro según MDR/IVDR." },
        { title: "Soporte poscomercialización", text: "Soporte con vigilancia poscomercialización, reclamaciones, informes de incidentes y acciones correctivas de seguridad." },
      ],
    },
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Proporcionamos servicios integrales de EU-REP que van más allá de la representación básica.",
    whyBenefits: [
      { title: "Representación integral", text: "Representación y comunicación oficial con todos los organismos regulatorios relevantes de la UE." },
      { title: "Verificación de documentación", text: "Verificación de tu Declaración de Conformidad y documentación técnica." },
      { title: "Cumplimiento del registro", text: "Aseguramos que hayas cumplido con todas las obligaciones de registro según MDR/IVDR." },
      { title: "Soporte poscomercialización", text: "Soporte con PMS, reclamaciones, informes de incidentes y FSCA." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group como tu EU-REP?",
    partnerIntro: "Con más de 300 clientes en todo el mundo, somos un proveedor líder de servicios de representante autorizado.",
    partnerBullets: [
      "Más de 300 clientes en todo el mundo confían en nuestros servicios de EU-REP.",
      "Sede en Bélgica con oficinas en toda la UE para una cobertura regulatoria completa.",
      "Más de 20 años de experiencia en cumplimiento de Medical Devices e IVD.",
      "Soporte de principio a fin más allá de la representación: estrategia, documentación y PMS.",
    ],
  },

  /* ───────── CH-REP ───────── */
  "swiss-authorized-representative": {
    title: "Representante autorizado de Suiza (CH-REP)",
    subtitle: "Acceso al mercado suizo de Medical Devices",
    description: "Los fabricantes con sede fuera de Suiza deben designar un representante autorizado suizo para comercializar Medical Devices e IVD en el mercado suizo.",
    whatTitle: "¿Qué es un CH-REP?",
    whatParagraphs: [
      "Los fabricantes con sede fuera de Suiza deben designar un <strong class='text-foreground'>representante autorizado suizo</strong> para comercializar Medical Devices e IVD en el mercado suizo.",
      "Desde la entrada en vigor de la nueva Ordenanza suiza sobre Medical Devices (MedDO) y la Ordenanza sobre In Vitro Diagnostics (IvDO), la designación de un CH-REP es obligatoria.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Qarad Suiza, parte de QbD Group, actúa como tu representante autorizado suizo de confianza.",
    whyBenefits: [
      { title: "Representación en el mercado suizo", text: "Actuamos como tu CH-REP oficialmente designado para el mercado suizo." },
      { title: "Verificación de documentación", text: "Verificamos que tu documentación técnica y Declaración de Conformidad cumplen los requisitos suizos." },
      { title: "Enlace con Swissmedic", text: "Gestionamos la comunicación con Swissmedic y respondemos a solicitudes de información." },
      { title: "Soporte de vigilancia", text: "Cooperamos con Swissmedic en acciones correctivas y de seguridad." },
    ],
    partnerTitle: "¿Por qué elegir Qarad Suiza?",
    partnerIntro: "Parte de QbD Group, Qarad Suiza es tu socio de confianza para el acceso al mercado suizo.",
    partnerBullets: [
      "Experiencia demostrada: más de 20 años de experiencia en representación de Medical Devices e IVD.",
      "Credibilidad regulatoria: presencia local en Suiza con profundo conocimiento del marco regulatorio suizo.",
      "Representación independiente: evita conflictos de interés que pueden surgir con importadores suizos.",
      "Soporte integral: requisitos específicos suizos, interacciones con Swissmedic y asesoramiento durante todo el ciclo de vida.",
    ],
  },

  /* ───────── UKRP ───────── */
  "ukrp-uk-responsible-person": {
    title: "Persona responsable del Reino Unido (UKRP)",
    subtitle: "Acceso al mercado de Medical Devices de Gran Bretaña",
    description: "Tras el Brexit, los fabricantes situados fuera del Reino Unido deben designar una persona responsable del Reino Unido para comercializar Medical Devices e IVD en el mercado de Gran Bretaña.",
    whatTitle: "¿Qué es un UKRP?",
    whatParagraphs: [
      "Tras el Brexit, los fabricantes situados fuera del Reino Unido deben designar una <strong class='text-foreground'>persona responsable del Reino Unido (UKRP)</strong> para comercializar Medical Devices e IVD en el mercado de Gran Bretaña.",
      "El rol de UKRP fue introducido tras el Brexit y es funcionalmente similar al representante autorizado de la UE bajo MDR/IVDR.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Qarad UK Ltd., parte de QbD Group, es tu socio de confianza para el acceso al mercado del Reino Unido.",
    whyBenefits: [
      { title: "Experiencia demostrada", text: "Más de 20 años de experiencia en representación de Medical Devices e IVD." },
      { title: "Credibilidad regulatoria", text: "Miembro de UKRPA promoviendo la conducta profesional y la competencia." },
      { title: "Representación independiente", text: "Te ayudamos a evitar conflictos de interés que pueden surgir al designar un importador del Reino Unido." },
      { title: "Soporte integral", text: "Orientación sobre requisitos específicos del Reino Unido, gestión de interacciones con la MHRA y asesoramiento regulatorio." },
      { title: "Alineación de doble mercado", text: "Apoyamos estrategias de acceso al mercado tanto de la UE como del Reino Unido a través de un único socio de confianza." },
    ],
    processTitle: "Nuestro proceso de incorporación en 3 pasos",
    processIntro: "Empezar con Qarad UK como tu UKRP es sencillo:",
    processSteps: [
      { title: "1. Mandato y configuración", text: "Formalizamos el acuerdo de UKRP y recopilamos la documentación." },
      { title: "2. Revisión de cumplimiento", text: "Evaluamos la preparación de la documentación para la MHRA." },
      { title: "3. Representación oficial", text: "Registramos tu producto y mantenemos las responsabilidades de UKRP." },
    ],
    partnerTitle: "¿Por qué elegir Qarad UK Ltd.?",
    partnerIntro: "Parte de QbD Group, Qarad UK Ltd. es tu socio de confianza para el acceso al mercado del Reino Unido.",
    partnerBullets: [
      "Experiencia demostrada: más de 20 años de experiencia en representación de Medical Devices e IVD.",
      "Credibilidad regulatoria: miembro de UKRPA promoviendo la conducta profesional y la competencia.",
      "Representación independiente: evita conflictos de interés con importadores del Reino Unido.",
      "Soporte integral: requisitos específicos del Reino Unido, interacciones con la MHRA y asesoramiento durante todo el ciclo de vida.",
      "Alineación de doble mercado: estrategias de acceso al mercado de la UE y el Reino Unido a través de un único socio de confianza.",
    ],
  },

  /* ───────── Linguistic Review ───────── */
  "linguistic-review": {
    title: "Revisión lingüística",
    subtitle: "Traducciones precisas y conformes bajo plazos ajustados",
    description: "Uno de los mayores retos en el proceso de revisión lingüística es el plazo de entrega extremadamente corto para las traducciones. QbD Group asegura traducciones de alta calidad que cumplen con los plazos procedimentales.",
    whatTitle: "¿Cuáles son los retos?",
    whatParagraphs: [
      "Uno de los mayores retos en el proceso de revisión lingüística es el <strong class='text-foreground'>plazo de entrega extremadamente corto</strong> para las traducciones. Después de que el CHMP emita su opinión sobre la solicitud de autorización de comercialización (MAA), dispones de solo <strong class='text-foreground'>5 días naturales</strong> para presentar las traducciones a los Estados miembros.",
      "Para mantenimiento (variaciones, renovaciones, medidas post-autorización), las traducciones deben presentarse en <strong class='text-foreground'>14 días naturales</strong> después de la opinión del CHMP o la decisión de la Comisión.",
      "Ya sea que prepares una PI en inglés para una nueva solicitud y la mantengas durante el ciclo de vida del producto o que asegures traducciones precisas en 25 idiomas de la UE/EEE, cada paso requiere precisión.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "En QbD Group, podemos ayudarte con estos requisitos regulatorios, asegurando traducciones de alta calidad que cumplen con los plazos procedimentales.",
    whyBenefits: [
      { title: "Traducciones multilingües", text: "Traducciones precisas en los 25 idiomas de la UE/EEE con precisión regulatoria." },
      { title: "Gestión de plazos ajustados", text: "Capacidad demostrada para entregar dentro de los estrictos plazos regulatorios de 5 y 14 días." },
      { title: "Gestión de información de producto", text: "Gestión completa del ciclo de vida de la PI desde la creación inicial hasta el mantenimiento." },
      { title: "Quality Assurance", text: "Controles de calidad rigurosos que aseguran la coherencia y el cumplimiento regulatorio." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Nuestro equipo de revisión lingüística combina experiencia regulatoria con precisión lingüística.",
    partnerBullets: [
      "Trayectoria demostrada en el cumplimiento de plazos de traducción regulatoria ajustados.",
      "Cobertura en los 25 idiomas de la UE/EEE.",
      "Profundo conocimiento de terminología farmacéutica y requisitos regulatorios.",
      "Integración fluida con tu proceso de presentación regulatoria.",
    ],
  },

  /* ───────── Legal Representative ───────── */
  "legal-representative": {
    title: "Representante legal",
    subtitle: "Representación en la UE para estudios de rendimiento",
    description: "Para los promotores de estudios de rendimiento con sede fuera de la Unión Europea, designar un representante legal dentro de la Unión es un requisito regulatorio. QbD Group proporciona representación experta en la UE.",
    whatTitle: "¿Qué es un representante legal?",
    whatParagraphs: [
      "Un representante legal es una persona física o jurídica establecida en la Unión Europea designada para actuar en nombre de un promotor no perteneciente a la UE en asuntos regulatorios relacionados con estudios de rendimiento.",
      "Para los promotores de estudios de rendimiento con sede fuera de la Unión Europea, designar un representante legal dentro de la Unión es un requisito regulatorio.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "QbD Group puede proporcionar a tu estudio la experiencia necesaria para guiarte a través del panorama regulatorio.",
    whyBenefits: [
      { title: "Representación regulatoria", text: "Actuamos como tu representante legal para estudios de rendimiento en la UE." },
      { title: "Presentaciones ante autoridades", text: "Gestionamos las presentaciones ante las autoridades competentes nacionales y los comités de ética." },
      { title: "Garantía de cumplimiento", text: "Aseguramos el pleno cumplimiento de los requisitos regulatorios de la UE para estudios de rendimiento." },
      { title: "Hub de comunicación", text: "Servimos como punto de contacto entre los promotores y las autoridades regulatorias de la UE." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Nuestros servicios de representante legal aseguran un acceso fluido al mercado de la UE para tus estudios de rendimiento.",
    partnerBullets: [
      "Entidad establecida en la UE con profunda experiencia regulatoria.",
      "Experiencia en múltiples áreas terapéuticas y tipos de dispositivos.",
      "Trayectoria demostrada en la gestión de interacciones regulatorias en la UE.",
      "Coordinación fluida entre promotores y autoridades competentes.",
    ],
  },

  /* ───────── IVDR Transition Support ───────── */
  "ivdr-transition-support": {
    title: "Soporte para la transición de IVDD a IVDR",
    subtitle: "Navega la transición de IVDD a IVDR con confianza",
    description: "Con plazos de certificación promedio de 13 a 18 meses y fechas límite de transición que se acercan rápidamente, los fabricantes de IVD no pueden permitirse esperar. Planificar con antelación asegura el cumplimiento y te da una ventaja competitiva.",
    whatTitle: "Por qué la acción temprana es clave",
    whatParagraphs: [
      "Con plazos de certificación promedio de 13 a 18 meses y fechas límite de transición que se acercan rápidamente, los fabricantes de IVD no pueden permitirse esperar. Planificar con antelación no solo asegura el cumplimiento, sino que te da una ventaja competitiva.",
      "Sin un plan claro, recursos adecuados y documentación conforme, incluso las organizaciones bien establecidas corren el riesgo de quedarse atrás.",
    ],
    helpCards: {
      heading: "Áreas clave impactadas",
      intro: "La transición de IVDD a IVDR impacta en múltiples áreas de tu organización:",
      cards: [
        { title: "Quality Assurance", text: "El certificado ISO 13485 no garantiza el cumplimiento del IVDR. Crea/actualiza procesos, procedimientos y plantillas." },
        { title: "Regulatory Affairs", text: "Define la estrategia regulatoria, los productos dentro del alcance y la estrategia de presentación a organismos notificados." },
        { title: "Documentación técnica", text: "Los nuevos requisitos del IVDR deben abordarse. Compila el STED listo para la revisión del NB." },
        { title: "Clínica y redacción médica", text: "Planes y estudios de evaluación del rendimiento. Validez científica y rendimiento analítico." },
      ],
    },
    whyTitle: "Nuestro enfoque de transición al IVDR",
    whyIntro: "Te apoyamos desde la evaluación inicial hasta la presentación final.",
    whyBenefits: [
      { title: "Evaluación de brechas", text: "Evaluación integral de brechas y auditorías simulacro para identificar áreas que necesitan atención." },
      { title: "Actualizaciones del QMS", text: "SOPs conformes con IVDR y actualizaciones del sistema de gestión de calidad." },
      { title: "Hoja de ruta regulatoria", text: "Hoja de ruta regulatoria clara incluyendo selección de NB, planificación y estrategia de presentación." },
      { title: "Soporte en documentación", text: "Documentación técnica, compilación del STED y soporte en la presentación." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Combinamos profunda experiencia regulatoria con experiencia práctica en la industria para ayudarte a cumplir los requisitos del IVDR.",
    partnerBullets: [
      "Trayectoria demostrada en transiciones exitosas al IVDR.",
      "Soporte de principio a fin desde la evaluación de brechas hasta la presentación al NB.",
      "Experiencia multifuncional: QA, RA, documentación técnica y clínica.",
      "Experiencia directa con los principales organismos notificados europeos.",
    ],
  },

  /* ───────── MDR Compliance & CE Certification ───────── */
  "mdr-compliance-ce-certification-for-medical-devices": {
    title: "Cumplimiento MDR y certificación CE",
    subtitle: "Alcanza y mantén el cumplimiento MDR para tus Medical Devices",
    description: "El Reglamento Europeo de Medical Devices establece un listón más alto para la seguridad, calidad y transparencia. Ya sea que estés llevando un nuevo dispositivo al mercado o haciendo la transición de uno existente, te guiamos en cada paso.",
    whatTitle: "El impacto real del cumplimiento MDR",
    whatParagraphs: [
      "El cumplimiento del MDR no es solo certificación. Asegura la seguridad del paciente, la continuidad del negocio y el acceso al mercado a largo plazo.",
      "El Reglamento Europeo de Medical Devices (MDR UE 2017/745) establece un listón más alto para la seguridad, calidad y transparencia. Ya sea que estés llevando un nuevo dispositivo al mercado o haciendo la transición de uno existente, lograr y mantener el cumplimiento del MDR requiere un enfoque integrado en <strong class='text-foreground'>Quality Assurance</strong>, <strong class='text-foreground'>Regulatory Affairs</strong> y <strong class='text-foreground'>documentación técnica</strong>.",
    ],
    helpCards: {
      heading: "Retos comunes",
      intro: "Muchas empresas enfrentan retos en su camino hacia el MDR:",
      cards: [
        { title: "Mayor supervisión regulatoria", text: "Interpretaciones en evolución y un escrutinio más intenso de los organismos notificados." },
        { title: "Deficiencias del QMS", text: "Brechas en los sistemas de gestión de calidad pueden obstaculizar la certificación." },
        { title: "Mayores exigencias clínicas", text: "Requisitos de evidencia aumentados y monitorización continua intensifican las cargas de trabajo." },
        { title: "Presión sobre los recursos", text: "Gestionar documentación, auditorías y presentaciones supone una presión significativa sobre los equipos internos." },
      ],
    },
    whyTitle: "Cómo apoyamos tu camino hacia el MDR",
    whyIntro: "QbD Group te guía en cada paso del proceso MDR, asegurando una certificación fluida y conforme.",
    whyBenefits: [
      { title: "Quality Assurance", text: "Evaluaciones de brechas del QMS, soporte de remediación, auditorías simulacro y liderazgo de auditorías de proveedores." },
      { title: "Regulatory Affairs", text: "Estrategia regulatoria, colaboración con NB, soporte de PRRC y servicios de representante autorizado." },
      { title: "Documentación técnica", text: "Abordamos todos los requisitos del MDR, compilamos documentación lista para la presentación al NB." },
      { title: "Evidencia clínica", text: "Soporte en la generación de evidencia clínica y estudios adicionales si son necesarios." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "Asóciate con QbD Group para abordar con confianza los retos del MDR y llevar tus dispositivos al mercado más rápido.",
    partnerBullets: [
      "+10 años de experiencia apoyando a empresas MedTech en toda Europa.",
      "Profunda experiencia en disciplinas de Regulatory Affairs, clínica y calidad.",
      "Colaboración directa con organismos notificados y asociaciones de la industria.",
      "Trayectoria demostrada en marcado CE y presentaciones MDR exitosas.",
    ],
  },

  /* ───────── MDR Transition Support ───────── */
  "mdr-transition-support": {
    title: "Soporte para la transición de MDD a MDR",
    subtitle: "Superando la brecha del MDD al MDR",
    description: "El cambio de la MDD al MDR es uno de los cambios regulatorios más significativos que la industria de Medical Devices ha enfrentado en décadas. QbD Group te ayuda a navegar esta transición.",
    whatTitle: "Por qué la acción temprana es clave",
    whatParagraphs: [
      "El cambio de la MDD al MDR es uno de los cambios regulatorios más significativos que la industria de Medical Devices ha enfrentado en décadas. Mientras la MDD permitía más flexibilidad, el MDR impone estándares más rigurosos para la seguridad, la evidencia clínica y la trazabilidad.",
      "<strong class='text-foreground'>El Reglamento de Medical Devices (MDR) no es solo un nuevo conjunto de reglas — es un nuevo terreno de juego.</strong> Un escrutinio más estricto, documentación aumentada y mayores expectativas para la evidencia clínica han cambiado la forma en que los Medical Devices se introducen en el mercado de la UE.",
    ],
    whyTitle: "Cómo te apoyamos",
    whyIntro: "En QbD Group, ayudamos a las empresas de Medical Devices a superar la brecha del MDD al MDR con orientación experta y soporte práctico.",
    whyBenefits: [
      { title: "Evaluación de brechas", text: "Evaluación integral de tu documentación MDD actual frente a los requisitos del MDR." },
      { title: "Estrategia de transición", text: "Hoja de ruta clara y accionable para tu recorrido de transición al MDR." },
      { title: "Actualización de documentación", text: "Actualización de tu documentación técnica para cumplir los estándares del MDR." },
      { title: "Coordinación con NB", text: "Gestión de las interacciones con organismos notificados y planificación de presentaciones." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Ya sea que navegues reglas de productos legacy o prepares tu primera presentación MDR, estamos aquí para hacer la transición lo más fluida y exitosa posible.",
    partnerBullets: [
      "Trayectoria demostrada en transiciones exitosas de MDD a MDR.",
      "Experiencia multifuncional que abarca QA, RA y documentación técnica.",
      "Enfoque pragmático alineado con tu cartera de productos y objetivos de negocio.",
      "Experiencia directa con las expectativas y requisitos de los organismos notificados.",
    ],
  },

  /* ───────── Coaching & Training ───────── */
  "coaching-training": {
    title: "Coaching y formación regulatoria",
    subtitle: "Desarrolla competencia regulatoria práctica dentro de tu equipo",
    description: "QbD Group ofrece programas de formación a medida, diseñados para fortalecer la comprensión y aplicación regulatoria práctica de tu equipo en MDR/IVDR.",
    whatTitle: "¿Qué es el coaching y la formación regulatoria?",
    whatParagraphs: [
      "Navegar el panorama regulatorio para Medical Devices e IVD requiere <strong class='text-foreground'>conocimiento especializado</strong> que evoluciona continuamente. Tanto si tu equipo es nuevo en MDR/IVDR como si necesita profundizar la experiencia existente, un coaching y formación específicos pueden acelerar significativamente tu preparación regulatoria.",
      "QbD Group ofrece <strong class='text-foreground'>programas de formación a medida</strong> diseñados para construir competencia regulatoria práctica dentro de tu organización. Nuestra formación va más allá de la teoría: nos centramos en la aplicación real, utilizando tus propios productos y documentación como casos de estudio siempre que sea posible.",
      "Desde talleres introductorios de un día hasta programas en profundidad de varias semanas, nuestra formación está diseñada para adaptarse a las necesidades y al calendario de tu equipo."
    ],
    helpCards: {
      heading: "Temas de formación",
      intro: "Nuestro portfolio de formación cubre una amplia gama de temas regulatorios para Medical Devices e IVD:",
      cards: [
        { title: "Fundamentos de MDR e IVDR", text: "Visión general de los Reglamentos europeos de Medical Devices y de Diagnóstico In Vitro." },
        { title: "Documentación técnica", text: "Orientación práctica para compilar y revisar documentación técnica conforme a MDR/IVDR." },
        { title: "Vigilancia y seguimiento poscomercialización", text: "Introducción a los requisitos de PMS y a los procesos de vigilancia bajo MDR/IVDR." },
        { title: "Sistemas de gestión de la calidad", text: "Formación sobre ISO 13485 y las expectativas de QMS bajo MDR/IVDR." },
      ],
    },
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Nuestros servicios de coaching y formación están diseñados para reforzar la comprensión y aplicación regulatoria práctica de tu equipo.",
    whyBenefits: [
      { title: "Programas personalizados", text: "Adaptamos el contenido de la formación a tus productos, mercados y al nivel de experiencia de tu equipo." },
      { title: "Formadores expertos", text: "Las sesiones las imparten profesionales regulatorios con amplia experiencia práctica en la industria." },
      { title: "Enfoque práctico", text: "Utilizamos ejemplos reales y casos de estudio, incluyendo materiales específicos del cliente cuando sea apropiado y acordado." },
      { title: "Refuerzo del aprendizaje", text: "Definimos objetivos de aprendizaje y podemos ofrecer evaluaciones opcionales o sesiones de seguimiento para favorecer la retención del conocimiento." },
      { title: "Soporte continuo", text: "Podemos proporcionar sesiones de seguimiento y soporte de Q&A para reforzar los temas clave después de la formación." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Los programas de formación de QbD Group se basan en una amplia experiencia práctica en el entorno regulatorio MedTech.",
    partnerBullets: [
      "Experiencia impartiendo formación regulatoria a startups, pymes y fabricantes multinacionales.",
      "Formación adaptada a diferentes niveles de madurez organizativa y tipos de producto.",
      "Formatos de impartición flexibles, incluidas sesiones presenciales, virtuales o híbridas.",
      "Soporte y coaching opcionales tras las sesiones de formación.",
    ],
  },

  /* ───────── Liaison with Notified Bodies ───────── */
  "liaison-with-notified-bodies": {
    title: "Liaison with Notified Bodies",
    subtitle: "Gestión estratégica de las interacciones con el Notified Body",
    description: "Interactuar con un Notified Body es un paso clave en el proceso de evaluación de la conformidad para Medical Devices e IVD. QbD Group acompaña a los fabricantes en cada etapa, ayudando a garantizar una comunicación estructurada, clara y alineada con la estrategia.",
    whatTitle: "¿Qué es el liaison with Notified Bodies?",
    whatParagraphs: [
      "Interactuar con un Notified Body es un <strong class='text-foreground'>paso importante</strong> en el proceso de evaluación de la conformidad para Medical Devices e IVD. La calidad y el momento de las interacciones con el Notified Body pueden tener un impacto significativo en el camino hacia el marcado CE.",
      "Muchos fabricantes subestiman la complejidad de las interacciones con el Notified Body. Desde el contacto inicial y la presentación de la solicitud hasta la respuesta a las preguntas de revisión y la gestión del seguimiento de auditorías, cada paso requiere <strong class='text-foreground'>atención regulatoria y una comunicación bien preparada</strong>.",
      "QbD Group acompaña a los fabricantes en sus interacciones con los Notified Bodies, ayudando a garantizar que las comunicaciones sean claras, completas y estén alineadas con la estrategia regulatoria global."
    ],
    helpCards: {
      heading: "Áreas clave de soporte",
      intro: "Nuestros servicios de liaison con Notified Bodies cubren todo el espectro de interacciones:",
      cards: [
        { title: "Compromiso inicial", text: "Te apoyamos en la selección de un Notified Body adecuado y en la preparación del paquete inicial de solicitud." },
        { title: "Gestión de consultas", text: "Coordinamos y apoyamos la preparación de respuestas a las preguntas del Notified Body, ayudando a garantizar respuestas puntuales y bien estructuradas." },
        { title: "Preparación de auditorías", text: "Apoyamos a tu equipo en la preparación para las auditorías del Notified Body, incluida la facilitación de auditorías simuladas y actividades de preparación." },
        { title: "Liaison continua", text: "Apoyamos una comunicación estructurada con tu Notified Body a lo largo del ciclo de vida del producto, ayudando a asegurar continuidad y claridad." },
      ],
    },
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Nuestro equipo aporta amplia experiencia apoyando las interacciones con Notified Bodies, facilitando una comunicación estructurada y eficiente durante todo el proceso.",
    whyBenefits: [
      { title: "Selección estratégica del NB", text: "Te orientamos en la elección del Notified Body más adecuado para tu producto, en línea con su finalidad prevista y su clasificación de riesgo." },
      { title: "Preparación de la solicitud", text: "Apoyamos la preparación de tu paquete de solicitud, ayudando a garantizar que sea completo, bien estructurado y alineado con las expectativas del Notified Body." },
      { title: "Gestión de plazos", text: "Ayudamos a planificar y supervisar los hitos clave, dando soporte al seguimiento puntual y ayudándote a mantener el ritmo durante el proceso de revisión." },
      { title: "Preparación para auditoría", text: "Realizamos evaluaciones de gaps y podemos facilitar auditorías simuladas para ayudarte a prepararte para las evaluaciones del Notified Body." },
      { title: "Coordinación de la comunicación", text: "Apoyamos la coordinación de las comunicaciones con el Notified Body, ayudando a garantizar claridad, coherencia y respuestas puntuales." },
    ],
    partnerTitle: "Tu partner de confianza",
    partnerIntro: "QbD Group cuenta con experiencia apoyando a los fabricantes en sus interacciones con Notified Bodies para Medical Devices e IVD.",
    partnerBullets: [
      "Experiencia trabajando con diversos Notified Bodies europeos.",
      "Sólida comprensión de los requisitos de evaluación de la conformidad bajo MDR e IVDR.",
      "Soporte de proyecto estructurado para facilitar y coordinar las interacciones con el Notified Body.",
      "Modelos de colaboración flexibles, desde soporte puntual y específico hasta una implicación más amplia en el proyecto.",
    ],
  },

  /* ───────── Vigilance ───────── */
  "materiovigilance": {
    title: "Vigilancia",
    subtitle: "Monitorización y notificación de incidentes para Medical Devices e IVD",
    description: "La vigilancia es la monitorización, evaluación y notificación sistemática de incidentes relacionados con Medical Devices e IVD tras su comercialización. QbD Group acompaña a los fabricantes en la implantación de sistemas de vigilancia conformes y eficientes.",
    whatTitle: "¿Qué es la vigilancia?",
    whatParagraphs: [
      "La vigilancia consiste en la <strong class='text-foreground'>monitorización, evaluación y notificación sistemática de incidentes</strong> relacionados con Medical Devices e IVD tras su comercialización. Forma parte de las obligaciones de seguimiento poscomercialización (PMS) bajo el Reglamento Europeo de Medical Devices (MDR) y el Reglamento de Diagnóstico In Vitro (IVDR).",
      "Los fabricantes deben implantar procesos de vigilancia para detectar, evaluar y notificar incidentes graves, acciones correctivas de seguridad en campo (FSCA) e informes de tendencias a las Autoridades Competentes correspondientes. <strong class='text-foreground'>El incumplimiento puede dar lugar a acciones regulatorias, incluidas retiradas u otras medidas de ejecución.</strong>",
      "QbD Group acompaña a los fabricantes en el desarrollo y mantenimiento de sistemas de vigilancia alineados con los requisitos de MDR e IVDR, integrados dentro de su marco de calidad y regulatorio global."
    ],
    helpCards: {
      heading: "Obligaciones clave de vigilancia",
      intro: "Bajo MDR e IVDR, los fabricantes deben implantar procesos que cubran:",
      cards: [
        { title: "Notificación de incidentes graves", text: "Notificación puntual de incidentes graves a las Autoridades Competentes dentro de los plazos regulatorios aplicables." },
        { title: "Notificación de tendencias", text: "Identificación y notificación de tendencias estadísticamente significativas en incidentes no graves." },
        { title: "Acciones Correctivas de Seguridad en Campo (FSCA)", text: "Planificación, coordinación y ejecución de acciones correctivas, incluida la comunicación mediante Notas de Seguridad en Campo cuando corresponda." },
        { title: "Mantenimiento del sistema de vigilancia", text: "Establecimiento y mantenimiento de un proceso sistemático para la detección, evaluación y notificación de incidentes dentro del marco de PMS." },
      ],
    },
    whyTitle: "Cómo te apoyamos",
    whyIntro: "Nuestros expertos en vigilancia ofrecen soporte estructurado en las áreas clave del proceso de vigilancia.",
    whyBenefits: [
      { title: "Diseño del sistema de vigilancia", text: "Apoyamos el diseño e implantación de sistemas de vigilancia adaptados a tu organización y cartera de productos." },
      { title: "Evaluación de incidentes", text: "Apoyamos la evaluación de incidentes frente a los criterios regulatorios de notificación y la preparación de envíos conformes." },
      { title: "Comunicación con autoridades", text: "Apoyamos las interacciones con las Autoridades Competentes, incluida la preparación y presentación de informes de incidentes y comunicaciones de seguridad." },
      { title: "Análisis de causa raíz", text: "Apoyamos las investigaciones de causa raíz y el desarrollo de acciones correctivas y preventivas." },
      { title: "Documentación de vigilancia", text: "Apoyamos el desarrollo y mantenimiento de procedimientos, plantillas y documentación relacionada con la vigilancia." },
    ],
    partnerTitle: "Tu partner de confianza",
    partnerIntro: "QbD Group combina experiencia regulatoria con experiencia práctica en procesos de vigilancia para Medical Devices e IVD.",
    partnerBullets: [
      "Experiencia apoyando actividades de vigilancia bajo MDR e IVDR.",
      "Participación práctica en la gestión de incidentes y en procesos FSCA.",
      "Enfoque integrado que conecta vigilancia, PMS y sistemas de gestión de la calidad.",
      "Modelos de soporte flexibles, desde asistencia puntual hasta un soporte de vigilancia más amplio.",
      "Formación para apoyar el desarrollo de capacidades internas.",
    ],
  },
};

const raOfferingsZh: Record<string, RAOfferingI18n> = {
  "regulatory-strategy-pharma": {
    title: "制药监管策略",
    subtitle: "为您的医药产品设计市场准入方案",
    description: "制定监管策略涉及为特定产品类型选择合适的程序、分析当地法规与要求、选择上市国家，并为产品上市时间提供建议。",
    whatTitle: "什么是监管策略",
    whatParagraphs: [
      "<strong class='text-foreground'>为您的医药产品设计市场准入方案。</strong>",
      "制定监管策略涉及为特定产品类型<strong class='text-foreground'>选择合适的程序</strong>、分析当地法规与要求、选择上市国家，并为产品上市时间提供建议。",
      "<strong class='text-foreground'>一项好的监管策略</strong>的多个组成部分与商业化和营销概念重叠。公司必须<strong class='text-foreground'>将监管策略融入其整体的开发、业务和运营战略中</strong>。若不在初期投入，通常会在后期产生负面影响。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "在 QbD Group，我们深知应对药物开发和监管路径中各种复杂性的挑战。我们的专业指导可确保您的项目从一开始就具备战略一致性。",
    whyBenefits: [
      {
        title: "一次成功",
        text: "从一开始就制定正确的策略。"
      },
      {
        title: "监管投入",
        text: "确保在您的药物开发项目中获得监管方面的专业意见。"
      },
      {
        title: "缩短上市时间",
        text: "选择正确的策略以缩短产品上市时间。"
      },
      {
        title: "与业务保持一致",
        text: "确保与业务期望保持一致。"
      },
      {
        title: "按时完成",
        text: "确保按时提交。"
      }
    ],
    partnerTitle: "为何与 QbD Group 合作",
    partnerIntro: "选择 QbD Group 意味着与 Regulatory Affairs 领域值得信赖的领导者合作。我们拥有十多年的经验，可在整个产品生命周期内提供定制化支持。",
    partnerBullets: [
      "+10 年经验：受益于我们高素质的顾问团队。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务覆盖：QbD Group 在全球范围内运营，其中 QbD 奥地利分部特别专注于欧洲（欧盟）和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "drug-development": {
    title: "药品开发",
    subtitle: "从发现到上市",
    description: "药品开发包括发现、临床前和临床测试。新化学实体在上市前必须证明其符合安全性、毒性、药代动力学和新陈代谢的最低标准。",
    whatTitle: "什么是药品开发？",
    whatParagraphs: [
      "<strong class='text-foreground'>从发现到上市：发现 ⟶ 临床前 ⟶ 临床试验</strong>",
      "药品开发包括发现、临床前和临床测试。新化学实体 (NCE) 在上市前必须在动物（临床前测试）和人体（临床试验）中证明其符合安全性、毒性、药代动力学和新陈代谢的最低标准，并<strong class='text-foreground'>按照高质量标准生产</strong>。",
      "药品开发过程漫长（长达 10 年）且成本高昂。只有少数 NCE 能够通过所有测试并成功上市。",
      "为确保成功上市，药品开发项目必须<strong class='text-foreground'>满足许可机构的法规要求</strong>。确保这一点的最佳方法是在流程早期制定稳健的法规策略。"
    ],
    whyTitle: "我们如何支持您",
    whyIntro: "将医药产品推向市场需要专业知识，以确保合规性、优化流程并取得成功。",
    whyBenefits: [
      {
        title: "法规合规",
        text: "我们指导您满足所有法规要求，确保您的产品符合许可机构的标准。"
      },
      {
        title: "优化临床前和临床测试",
        text: "我们确保您的测试全面且适当，并根据您产品的特定需求量身定制。"
      },
      {
        title: "定制化测试策略",
        text: "我们帮助您区分并满足各类产品的测试要求，包括 NCE、产品线扩展、仿制药和新适应症。"
      }
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "选择 QbD Group 意味着与 Regulatory Affairs 领域值得信赖的领导者合作。凭借十多年的经验，我们在整个产品生命周期内提供定制化支持。",
    partnerBullets: [
      "10年以上经验：受益于我们资深顾问团队的专业知识。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务覆盖：QbD Group 在全球范围内运营 专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "early-development": {
    title: "早期开发",
    subtitle: "驾驭药物开发的早期阶段",
    description: "潜在药物早期开发的一个关键步骤是为首次人体研究准备临床试验申请。",
    whatTitle: "什么是早期开发？",
    whatParagraphs: [
      "潜在药物早期开发的一个关键步骤是为首次人体 (FIH) 研究准备临床试验申请。",
      "QbD Group 的专家将指导您完成此过程及后续步骤，例如，确保遵守欧盟儿科法规的要求，抓住获得孤儿药资格的机会，并通过科学建议程序积极寻求监管机构的意见。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们为您的临床开发提供无缝协助，帮助您准备关键文档并应对监管要求。",
    whyBenefits: [
      {
        title: "FIH 临床试验申请",
        text: "我们协助准备和提交 FIH 试验申请，确保符合监管要求。"
      },
      {
        title: "文档准备",
        text: "我们的团队确保所有必要文档（包括 CTA 和 IMPD）的准确性、完整性和合规性。"
      },
      {
        title: "孤儿药和儿科计划",
        text: "我们支持您获得孤儿药资格认定和制定儿科研究计划。"
      },
      {
        title: "PRIME 申请和科学建议",
        text: "我们为 PRIME 申请和科学建议提供专业指导。"
      }
    ],
    helpCards: {
      heading: "早期开发策略",
      intro: "驾驭药物开发的早期阶段对于为成功奠定坚实基础至关重要。",
      cards: [
        {
          title: "早期阶段开发",
          text: "为您的药物开发项目最早阶段提供全面支持。"
        },
        {
          title: "临床试验申请",
          text: "准备和提交 CTA、方案、IB 和 IMPD。"
        },
        {
          title: "孤儿药资格认定",
          text: "支持获得孤儿药资格认定 (ODD)。"
        },
        {
          title: "儿科研究计划",
          text: "遵守欧盟儿科法规的要求。"
        }
      ]
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与 Regulatory Affairs 领域值得信赖的领导者合作。",
    partnerBullets: [
      "+10 年经验：我们高素质的顾问团队让您尽享专业优势。",
      "全生命周期支持：从上市前到上市后阶段的全面支持。",
      "全球业务布局：QbD Group 的业务遍及全球，其中 QbD 奥地利公司尤其专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "scientific-advice": {
    title: "科学建议",
    subtitle: "与监管机构互动的专家指导",
    description: "科学建议程序在药品审批流程中起着核心作用。我们经验丰富的顾问将帮助您与所有相关的卫生监管机构进行互动。",
    whatTitle: "科学建议的类型",
    whatParagraphs: [
      "在潜在药物开发的早期阶段，一个关键步骤是为首次人体 (FIH) 研究准备临床试验申请。",
      "QbD Group 的专家将指导您完成此流程及后续步骤，确保符合欧盟儿科法规的要求，抓住孤儿药资格的机会，并积极寻求监管机构的意见。"
    ],
    extraSections: [
      {
        title: "这包括什么？",
        paragraphs: [
          "<strong class='text-foreground'>与机构互动</strong> — 成功向主管当局提交您的药品审批申请。",
          "<strong class='text-foreground'>准备与沟通</strong> — 与机构互动在药品审批流程中起着核心作用。根据程序，我们经验丰富的顾问团队将协助您与所有相关的卫生监管机构进行互动。"
        ]
      }
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们提供一系列专业服务，以支持您与监管机构的互动。",
    whyBenefits: [
      {
        title: "启动与监管机构的联系",
        text: "我们协助建立与监管机构的初步联系。"
      },
      {
        title: "准备简报文件包",
        text: "我们的团队准备全面的简报文件包，以支持监管提交。"
      },
      {
        title: "主持与监管机构的会议",
        text: "我们高效地管理和主持与监管机构的会议。"
      },
      {
        title: "档案指导 (CMC)",
        text: "我们提供化学、制造和控制 (CMC) 档案方面的专家指导。"
      }
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与 Regulatory Affairs 领域值得信赖的领导者合作。",
    partnerBullets: [
      "超过 10 年的经验：受益于我们高素质的顾问团队。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球范围内开展业务 特别专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "regulatory-procedures": {
    title: "监管程序",
    subtitle: "驾驭欧盟和英国的注册途径",
    description: "在欧盟和英国，有多种药品注册程序可供选择。选择哪种方法取决于药品类型和预期的上市途径。",
    whatTitle: "欧盟和英国的监管程序",
    whatParagraphs: [
      "在欧盟和英国，有多种药品注册程序可供选择。<strong class='text-foreground'>所选方法取决于药品类型</strong>（非处方药 – OTC、处方药 – Rx、仿制药、新化学实体、生物技术衍生产品等）和预期的上市途径。",
      "<strong class='text-foreground'>欧盟立法为特定类型的产品规定了某些程序</strong>，例如，对于生物技术衍生产品、孤儿药以及针对艾滋病、癌症、神经退行性疾病和糖尿病等特定疾病群体的药物，必须采用集中审评程序。",
      "我们在英国的团队还可以为您提供英国/大不列颠（UK/GB）国家申请和国际认可程序的支持。",
      "最后，我们的专家还可以支持您将药品推向美国市场。"
    ],
    helpCards: {
      heading: "程序类型",
      intro: "我们指导您了解欧盟和英国的每一种可用注册途径：",
      cards: [
        {
          title: "国家程序",
          text: "如果您希望在单个成员国获得许可，需向该国主管当局提交申请。"
        },
        {
          title: "互认程序 (MRP)",
          text: "适用于希望在多个成员国注册的产品。该程序基于一个成员国（参照成员国/RMS）已批准的国家许可，在其他欧盟国家（相关成员国/CMS）获得相互认可。"
        },
        {
          title: "分散审评程序 (DCP)",
          text: "遵循与 MRP 相同的原则。当产品尚未在任何成员国获得上市许可时使用此程序。"
        },
        {
          title: "集中审评程序 (CP)",
          text: "向 EMA 提交申请，以获得在整个欧盟有效的共同体许可。对于生物技术产品、孤儿药以及治疗艾滋病、癌症、神经退行性疾病和糖尿病的产品，此程序是强制性的。"
        }
      ]
    },
    extraSections: [
      {
        title: "产品线扩展",
        paragraphs: [
          "法规 (EC) No 1084/2003（MRP 或 DCP）和法规 (EC) No 1085/2003（针对 CP）的附件 II 中描述了某些申请类型，这些申请既不属于“变更”的范围，但同时也不是全新的申请。"
        ]
      },
      {
        title: "英国/大不列颠（UK/GB）国家申请与国际认可程序 (IRP)",
        paragraphs: [
          "申请人可以通过国家申请或使用 IRP 的方式，为已获得 MHRA 指定的参考国家/地区之一批准的药品申请英国上市许可。"
        ]
      }
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "驾驭复杂的监管环境可能是一项挑战，但在我们的专业指导下，您可以充满信心地前进。",
    whyBenefits: [
      {
        title: "选择合适的程序",
        text: "我们帮助您根据产品类型和市场策略选择正确的注册途径。"
      },
      {
        title: "提供监管策略建议",
        text: "制定与您的业务目标和产品组合相一致的战略性监管规划。"
      },
      {
        title: "联络监管机构",
        text: "我们代表您与主管当局建立并管理沟通。"
      },
      {
        title: "组织和主持会议",
        text: "我们协调并主持与监管机构的会议，以确保取得富有成效的结果。"
      }
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与一位在 Regulatory Affairs 领域拥有十多年经验、值得信赖的领导者合作。",
    partnerBullets: [
      "+10 年经验：我们的高素质顾问团队将为您带来裨益。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球范围内运营 专注于欧洲（欧盟）和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "submissions": {
    title: "监管递交",
    subtitle: "助您一次性成功获得上市许可",
    description: "在复杂的监管环境中申请上市许可，需要满足严格的文件和合规要求。我们确保您的申请符合所有监管标准。",
    whatTitle: "欧盟申请类型",
    whatParagraphs: [
      "在复杂的监管环境中申请上市许可，需要满足严格的文件和合规要求。无论是为原研产品准备完整申请，为仿制药准备简化申请，还是为固定组合产品准备卷宗，精确性都至关重要。",
      "在 QbD Group，我们专注于帮助您一次性成功完成递交。凭借我们的专业知识，我们确保您的申请符合所有监管标准。"
    ],
    helpCards: {
      heading: "申请类型",
      intro: "了解欧盟的申请类型以及我们如何为每种类型提供支持：",
      cards: [
        {
          title: "完整申请",
          text: "对于完整申请，上市许可申请必须附有 2001/83/CE 指令第 8 条规定的数据和文件。"
        },
        {
          title: "简化申请 (仿制药)",
          text: "无论是获得原研药厂的知情同意，还是与已获批 6 至 10 年的产品基本相似。"
        },
        {
          title: "固定组合产品",
          text: "任何固定组合产品都是一种新的、独特的药品，需要单独的上市许可和产品特性摘要。"
        },
        {
          title: "文献申请",
          text: "为成熟应用的申请提供支持。"
        }
      ]
    },
    whyTitle: "我们如何为您提供支持",
    whyIntro: "您的上市许可申请必须正确附上所需的文件。",
    whyBenefits: [
      {
        title: "卷宗内容和结构建议",
        text: "提供符合监管期望的专业卷宗内容和结构指导。"
      },
      {
        title: "差距分析",
        text: "进行全面的差距分析，在递交前识别并解决文件缺陷。"
      },
      {
        title: "技术卷宗汇编",
        text: "完整汇编符合所有监管要求的技术卷宗。"
      },
      {
        title: "可随时递交的申请包",
        text: "准备完整的申请包，可随时递交给欧盟监管机构。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与一家在 Regulatory Affairs 领域拥有十多年经验、值得信赖的领导者合作。",
    partnerBullets: [
      "+10 年经验：我们的高素质顾问团队将为您带来裨益。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球开展业务，其中奥地利 QbD 尤其专注于欧洲 (欧盟) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "ectd-publishing": {
    title: "eCTD 发布",
    subtitle: "电子申报，精准无误",
    description: "eCTD 基于 CTD 格式，以电子形式将行业法规信息传输给监管机构。",
    whatTitle: "电子通用技术文件 (eCTD)",
    whatParagraphs: [
      "电子通用技术文件 (eCTD) 基于通用技术文件 (CTD) 格式的内容，<strong class='text-foreground'>以电子形式将行业法规信息传输给监管机构</strong>。",
      "<strong class='text-foreground'>所有类型的法规信息均可以电子格式提交</strong>，包括国家程序、互认程序、分散程序和集中程序的新申请、变更和续期。"
    ],
    extraSections: [
      {
        title: "如何创建 eCTD",
        paragraphs: [
          "eCTD 中的文件必须根据 ICH eCTD 规范 3.2 版和当前版本的欧盟模块 1 (EU Module 1) 规范进行组织。",
          "为使用 eCTD，上市许可持有人 (MAH) 必须投资于<strong class='text-foreground'>适当的软件</strong>并维护支持系统。"
        ]
      }
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "管理 eCTD 申报需要精确性、专业经验和合适的工具。在 QbD Group，我们提供全面的支持。",
    whyBenefits: [
      {
        title: "验证和选择 eCTD 软件",
        text: "我们帮助您评估、验证和选择最合适的 eCTD 软件。"
      },
      {
        title: "编译准确且合规的 eCTD",
        text: "为所有程序类型完成 eCTD 申报的完整编译和发布。"
      },
      {
        title: "准备和提交电子申请",
        text: "向监管机构提供端到端的电子申请准备和提交服务。"
      },
      {
        title: "持续的 eCTD 维护和支持",
        text: "持续的 eCTD 生命周期支持，包括变更、续期和技术验证。"
      }
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与在 Regulatory Affairs 领域拥有十多年经验、值得信赖的领导者合作。",
    partnerBullets: [
      "超过10年的经验：受益于我们高素质的顾问团队。",
      "全生命周期支持：从上市前到上市后阶段的全面支持。",
      "全球业务布局：QbD Group 在全球范围内运营 特别专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "artwork-labeling": {
    title: "包装与标签",
    subtitle: "合规营销",
    description: "欧盟指令 2001/83/CE 要求标签文本必须与经批准的产品特性概要 (SmPC) 一致。QbD Group 帮助您创建完全合规的药品包装图稿。",
    whatTitle: "合规营销",
    whatParagraphs: [
      "<strong class='text-foreground'>欧盟指令 2001/83/CE 要求标签文本必须与经批准的产品特性概要 (SmPC) 一致。</strong>",
      "对于集中注册的产品，只有一个 SmPC；对于互认或分散注册程序，SmPC 在产品注册国之间是协调统一的。",
      "对于可能存在不同 SmPC 的“旧”国家许可证，标签和说明书的文本可能因国家/地区而异。"
    ],
    extraSections: [
      {
        title: "软件要求",
        paragraphs: [
          "<strong class='text-foreground'>文档管理软件 + 变更控制流程：</strong>",
          "• 包装材料的适当归档和电子文档管理<br/>• 修订号<br/>• 变更控制<br/>• 工作流程管理<br/>• 版本控制<br/>• SOPs<br/>• 提供基于 XML 的编写工具"
        ]
      }
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "准确、合规且易于使用的产品信息对于满足法规标准和确保患者安全至关重要。",
    whyBenefits: [
      {
        title: "准备合规的说明书 (PIL) 和标签文本",
        text: "根据经批准的 SmPC 和法规要求，创建并审核说明书 (PIL) 和标签文本。"
      },
      {
        title: "监督翻译项目",
        text: "协调和监督您目标市场所需的所有语言的翻译项目。"
      },
      {
        title: "审核说明书 (PIL) 和标签文本的合规性",
        text: "进行全面审核，确保符合欧盟指令 2001/83/CE 的要求。"
      },
      {
        title: "提供关于说明书 (PIL) 用户测试的专家建议",
        text: "提供用户测试方法的专业指导，以确保患者能够理解。"
      }
    ],
    partnerTitle: "为何与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与一家在 Regulatory Affairs 领域拥有十余年经验、值得信赖的领先企业合作。",
    partnerBullets: [
      "+10 年经验：从我们高素质的顾问团队中获益。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球范围内运营 尤其专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "lifecycle-management": {
    title: "生命周期管理",
    subtitle: "产品整个生命周期内的变更管理",
    description: "已上市的产品会随着时间的推移而发生变化，这一过程必须得到妥善管理。QbD Group 的专家将在整个生命周期管理过程中为您提供支持。",
    whatTitle: "应对变革",
    whatParagraphs: [
      "已上市的产品会<strong class='text-foreground'>随着时间的推移而发生变化</strong>，在其经历不同阶段的过程中，这一<strong class='text-foreground'>流程必须得到妥善管理</strong>。",
      "在制药行业，对于变更类型和文档要求有明确的指导方针。",
      "对于通过 MRP 或 DCP 注册的产品，任何变更都必须同时提交给产品上市所在成员国的主管当局。",
      "使用变更控制软件解决方案有助于有效控制所有注册后的变更。",
      "在西班牙，要让您的产品成功上市，实现基于价值的定价是一项挑战。QbD Group 能够根据法规策略并遵循包括定价和报销在内的国家要求，为您的产品上市提供支持。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "适应法规变化可能非常复杂。在 QbD Group，我们提供量身定制的解决方案，确保您的申报材料符合最高标准。",
    whyBenefits: [
      {
        title: "准备变更申请",
        text: "确保变更申报所需的所有文件准确、完整。"
      },
      {
        title: "向当局提交",
        text: "高效地管理变更申请并将其提交给主管当局。"
      },
      {
        title: "实施变更控制",
        text: "建立并维护一个稳健的系统，用于跟踪和管理变更。"
      },
      {
        title: "培训您的团队",
        text: "为您的专业人员提供有关法规流程的实践培训。"
      },
      {
        title: "软件咨询",
        text: "推荐并实施合适的工具来管理法规事务。"
      },
      {
        title: "管理序列化",
        text: "顺利管理国家代码和序列化要求。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着选择与在 Regulatory Affairs 领域拥有十余年经验、值得信赖的领导者合作。",
    partnerBullets: [
      "+10 年以上经验：我们高素质顾问团队的丰富经验将为您带来裨益。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务覆盖：QbD Group 在全球范围内开展业务 专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "information-officer": {
    title: "信息负责人",
    subtitle: "药品信息和营销合规管理",
    description: "为完全符合法律要求，必须由合格的专家负责药品信息的沟通和营销方式。",
    whatTitle: "管理您的信息",
    whatParagraphs: [
      "<strong class='text-foreground'>制药公司在宣传其产品时可以做到什么程度？产品特性摘要 (SmPC) 中必须传达哪些信息？</strong>",
      "为完全符合法律要求，必须由合格的专家负责药品信息的沟通和营销方式。",
      "<strong class='text-foreground'>信息负责人的职责：</strong>",
      "• 必须具备<strong class='text-foreground'>扎实的医学和科学领域知识</strong>，以便正确应用所有相关指南。<br/>• <strong class='text-foreground'>负责</strong>确保标签、说明书、产品信息和广告与其批准或注册保持一致。<br/>• 必须<strong class='text-foreground'>随时了解</strong>法规、法律和时间表的最新变更，并相应地执行。<br/>• 根据需要与相关监管机构<strong class='text-foreground'>进行协调</strong>。<br/>• <strong class='text-foreground'>举办培训课程</strong>，以提高销售和营销人员对适用法律法规的认识。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "制药公司在产品沟通和营销方面必须在严格的法律框架内运营。",
    whyBenefits: [
      {
        title: "行业专长",
        text: "我们的团队在制药和生命科学领域拥有多年的经验。"
      },
      {
        title: "全面的解决方案",
        text: "我们确保您的所有材料，包括标签、PIL、SmPC 和广告材料，均符合法律要求。"
      },
      {
        title: "赋能您的团队",
        text: "通过有针对性的培训课程，我们帮助您的销售和营销团队掌握所需知识，以便在法律限制内开展工作。"
      }
    ],
    partnerTitle: "为何与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 意味着与一家在 Regulatory Affairs 领域拥有十余年经验、值得信赖的领导者合作。",
    partnerBullets: [
      "+10 年经验：受益于我们高素质的顾问团队。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球范围内运营 特别专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "orphan-diseases": {
    title: "罕见病",
    subtitle: "支持罕见病药物的开发",
    description: "孤儿病是影响少数人群的疾病。QbD Group 可支持您为罕见病患者开发药物。",
    whatTitle: "什么是孤儿病？",
    whatParagraphs: [
      "孤儿病，又称<strong class='text-foreground'>罕见病</strong>，是<strong class='text-foreground'>指影响少数人群的疾病</strong>。",
      "尽管罕见，但已发现的孤儿病超过 7,000 种，全球共有数百万患者。<strong class='text-foreground' >由于商业利益有限，这些疾病通常缺乏有效的治疗方法</strong>，因此开发孤儿药对于提高患者的生活质量至关重要。",
      "为鼓励该领域的创新，EMA 等监管机构提供孤儿药资格认定 (ODD)，为制药公司提供以下激励措施：",
      "• 延长市场独占期<br/>• 降低监管费用<br/>• 通过方案协助提供专业的科学指导",
      "QbD Group 在此帮助您应对孤儿药开发中的复杂挑战。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "在 QbD Group，我们为孤儿药资格认定 (ODD) 申请和持续合规提供端到端的支持。",
    whyBenefits: [
      {
        title: "ODD 申请专家支持",
        text: "我们为 ODD 申请流程的每个阶段提供协助，包括申报材料准备和申报前会议。"
      },
      {
        title: "资格认定后的合规",
        text: "我们的团队确保您履行所有资格认定后的义务，例如年度报告和 ODD 状态维护。"
      },
      {
        title: "定制化指导",
        text: "凭借我们深厚的法规经验，我们提供定制化策略，以最大限度地利用激励措施。"
      }
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "选择 QbD Group 意味着与一家在 Regulatory Affairs 领域拥有十多年经验、值得信赖的领先企业合作。",
    partnerBullets: [
      "+10 年经验：我们的高素质顾问团队将为您带来裨益。",
      "全生命周期支持：从上市前到上市后阶段的全方位支持。",
      "全球业务布局：QbD Group 在全球范围内开展业务 专注于欧洲 (EU) 和中国市场。",
      "客户满意度：持续评估、高透明度和明确的目标。"
    ]
  },

  "regulatory-strategy-md-ivd": {
    title: "医疗器械和IVD的法规策略",
    subtitle: "医疗器械和IVD市场准入的战略性方法",
    description: "法规策略是一个重要框架，它概述了将医疗器械或IVD推向市场所需遵循的法规要求的必要步骤和行动。",
    whatTitle: "什么是医疗器械和IVD的法规策略？",
    whatParagraphs: [
      "法规策略是一个重要框架，它概述了将<strong class='text-foreground'>医疗器械或IVD推向市场</strong>所需遵循的法规要求的必要步骤和行动。",
      "这种战略性方法使制造商能够高效地应对复杂的法规环境，确保合规并促进产品及时进入市场。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "在QbD Group，我们帮助您为您的医疗器械或IVD制定可靠的法规策略。",
    whyBenefits: [
      {
        title: "器械分类",
        text: "我们根据MDR或IVDR确定您器械的正确分类。"
      },
      {
        title: "法规路线图",
        text: "我们创建与您业务目标相一致的清晰法规路线图。"
      },
      {
        title: "认证机构选择",
        text: "我们指导您选择合适的认证机构。"
      },
      {
        title: "文件规划",
        text: "我们为合规所需的技术文件进行规划。"
      }
    ],
    partnerTitle: "为何选择与QbD Group合作？",
    partnerIntro: "QbD Group将深厚的法规专业知识与医疗科技行业的实践知识相结合。",
    partnerBullets: [
      "在医疗器械和IVD法规策略方面拥有数十年的经验。",
      "与欧洲主要认证机构合作的直接经验。",
      "与您的商业目标相一致的务实方法。",
      "从分类到认证的全方位支持。"
    ]
  },

  "in-country-representative-services": {
    title: "境内代表服务",
    subtitle: "EU-REP、UKRP 和 CH-REP 服务",
    description: "我们的法规专家可担任 EU-REP/UKRP/CH-REP，帮助您的 IVD 和医疗器械进入欧洲、英国和瑞士市场。",
    whatTitle: "什么是授权代表？",
    whatParagraphs: [
      "授权代表是在特定司法管辖区内设立的法律实体，代表<strong class='text-foreground'>非欧盟/非英国/非瑞士制造商</strong>行事。它是制造商与当地主管当局之间的主要联络点。"
    ],
    helpCards: {
      heading: "我们提供的服务",
      intro: "我们的法规专家可担任 EU-REP/UKRP/CH-REP，帮助您的 IVD 和医疗器械进入欧洲/英国/瑞士市场。",
      cards: [
        {
          title: "文件验证",
          text: "确保符合性声明和技术文件均已备齐。"
        },
        {
          title: "合规确认",
          text: "核实制造商已履行注册义务。"
        },
        {
          title: "标签与识别",
          text: "确认器械上标明了制造商和授权代表的名称与地址。"
        },
        {
          title: "事件和投诉管理",
          text: "作为与欧盟/英国/瑞士当局沟通的主要联络点。"
        },
        {
          title: "与当局沟通",
          text: "提供所需文件并保存合规记录。"
        },
        {
          title: "市场监督支持",
          text: "与当局合作，执行纠正措施、市场监督和器械召回。"
        }
      ]
    },
    processTitle: "超越代表服务：您的全方位合规合作伙伴",
    processIntro: "我们的授权代表服务是一个<strong class='text-foreground'>全面整合的法规和质量框架</strong>的一部分，可确保您的器械在每个阶段都符合法规要求。",
    processSteps: [
      {
        title: "Regulatory Affairs 支持",
        text: "提供战略性法规支持，满足全方位的合规需求。"
      },
      {
        title: "质量与审计服务",
        text: "提供独立的内部、供应商或模拟审计，以验证您的质量管理体系 (QMS)。"
      },
      {
        title: "上市后监督",
        text: "支持上市后义务的监控，并协助监督流程。"
      },
      {
        title: "培训与合规审查",
        text: "联系经验丰富的 RA 和 QA 专业人员，获得专业培训和战略建议。"
      }
    ],
    whyTitle: "信心之选：为何选择 QbD Group 作为您的授权代表",
    whyIntro: "我们为欧洲主要市场提供经验丰富、独立且完全合规的代表服务。",
    whyBenefits: [
      {
        title: "多市场经验",
        text: "我们确保完全遵守欧盟、英国和瑞士的 MDR/IVDR 法规。"
      },
      {
        title: "超越代表服务",
        text: "我们不仅满足法律要求，还提供包括法规策略和技术文件在内的全面支持。"
      },
      {
        title: "跨境法规专业知识",
        text: "我们在欧盟、英国和瑞士拥有广泛的法规顾问网络。"
      },
      {
        title: "直接、专业的沟通",
        text: "您将与经验丰富的法规专业人员直接合作——快速、透明、个性化。"
      },
      {
        title: "值得信赖的准入合作伙伴",
        text: "作为您的 EU-REP、UKRP 和 CH-REP，我们是您与当局沟通的官方联络点。"
      },
      {
        title: "服务覆盖所有器械类别",
        text: "我们为所有类别的器械提供一致、可靠的授权代表服务。"
      }
    ],
    partnerTitle: "为何选择 QbD Group？",
    partnerIntro: "QbD Group 是您在欧洲寻求授权代表服务的可靠伙伴。",
    partnerBullets: [
      "全球视野，本地专长：我们在欧洲各地设有法规中心，提供智能且可扩展的解决方案。",
      "卓越的法规能力：在 Qarad 等行业领导者的支持下，我们拥有超过 20 年的丰富经验。",
      "端到端支持：从 CE 标志到上市后义务，我们提供全程支持。",
      "个性化与前瞻性方法：我们的团队与您的团队密切合作，使法规策略与您的业务目标保持一致。"
    ]
  },

  "technical-documentation": {
    title: "技术文档",
    subtitle: "针对医疗器械和 IVD 的全面监管文档",
    description: "针对 IVD 和医疗器械的新法规对技术文档和临床证据提出了更严格的要求。我们拥有二十多年的经验，可提供无与伦比的专业支持。",
    whatTitle: "医疗器械和 IVD 技术文档",
    whatParagraphs: [
      "针对 IVD 和医疗器械的新法规对技术文档和临床证据提出了<strong class='text-foreground'>更严格的要求</strong>。",
      "凭借二十多年的经验，我们可为您编写符合 MDR 和 IVDR 要求的技术文档提供无与伦比的专业支持。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们为医疗器械和 IVD 的技术文档提供全面的支持。",
    whyBenefits: [
      {
        title: "STED 编写",
        text: "为提交给公告机构的文件完整编写简要技术文档 (Summary Technical Documentation, STED)。"
      },
      {
        title: "差距分析",
        text: "根据 MDR/IVDR 的要求，对现有文档进行全面评估。"
      },
      {
        title: "器械描述",
        text: "详细的器械文档，包括工作原理和技术规格。"
      },
      {
        title: "生物学评估",
        text: "根据 ISO 10993 标准提供生物相容性评估支持。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "QbD Group 是您值得信赖的合作伙伴，擅长处理符合 MDR 和 IVDR 要求的技术文档。",
    partnerBullets: [
      "在医疗器械和 IVD 的技术文档方面拥有超过 20 年的经验。",
      "深入了解公告机构的要求和期望。",
      "采用全面的方法，涵盖 MDR/IVDR 的所有附件。",
      "拥有帮助客户成功获得 CE 认证的可靠业绩记录。"
    ]
  },

  "regulatory-intelligence": {
    title: "法规情报",
    subtitle: "在法规领域保持领先",
    description: "法规情报对于成功的产品开发和有效的法规策略至关重要。利用最新、最全面的信息，您可以在竞争中保持领先。",
    whatTitle: "我们提供的服务",
    whatParagraphs: [
      "法规情报对于成功的产品开发和有效的法规策略至关重要。利用最新、最全面的信息，您不仅可以<strong class='text-foreground'>在竞争中保持领先</strong>，还能确保您的产品完全符合当前及不断变化的法规要求。",
      "<strong class='text-foreground'>法规情报可包括：</strong>",
      "• 法规信息<br/>• 竞争对手信息<br/>• 法律要求信息<br/>• 立法更新"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "QbD Group 提供全面的法规情报服务，以满足医疗器械制造商不断变化的需求。",
    whyBenefits: [
      {
        title: "法规信息",
        text: "我们监控并分析法规更新，确保您及时获得信息。"
      },
      {
        title: "明确法律要求",
        text: "我们的专家帮助解读复杂的法规，指导您遵循所有适用的法律标准。"
      },
      {
        title: "定制化立法支持",
        text: "我们针对特定的立法更新提供建议，确保您在任何变化面前都能保持领先。"
      },
      {
        title: "标准演变管理",
        text: "我们跟踪新的和过时的标准，帮助您适应最新的要求。"
      },
      {
        title: "全面的信息包",
        text: "我们详细的信息包可为任何法规查询提供详尽的分析。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "QbD Group 提供定制的法规情报，让您随时了解最新信息并确保合规。",
    partnerBullets: [
      "拥有深厚行业知识的专业法规专家团队。",
      "主动监控关键市场的法规变化。",
      "根据您的特定产品组合量身定制报告。",
      "将法规情报与业务规划相结合的战略指导。"
    ]
  },

  "design-and-development": {
    title: "设计与开发",
    subtitle: "从概念到上市，设计安全有效的医疗器械",
    description: "设计与开发是将安全有效的医疗器械和 IVD 推向市场的核心环节。我们的团队在整个设计周期内提供专业指导。",
    whatTitle: "什么是医疗器械的设计与开发？",
    whatParagraphs: [
      "设计与开发是将 <strong class='text-foreground'>安全有效的医疗器械和 IVD 推向市场</strong> 的核心环节。",
      "我们的团队在整个设计周期内提供专业指导，确保从最初概念阶段起就符合法规要求。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们在您的器械的整个设计与开发过程中提供全面的支持。",
    whyBenefits: [
      {
        title: "设计规划",
        text: "制定符合 ISO 13485 和 IEC 62304 的设计方案。"
      },
      {
        title: "风险管理",
        text: "根据 ISO 14971 标准，将风险分析整合到设计流程中。"
      },
      {
        title: "验证与确认",
        text: "规划并执行设计验证与确认活动。"
      },
      {
        title: "技术文档",
        text: "编写符合 MDR/IVDR 法规的技术文档。"
      }
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "QbD Group 在医疗器械设计与开发领域拥有数十年的经验。",
    partnerBullets: [
      "在 ISO 13485、ISO 14971 和 IEC 62304 方面拥有深厚的专业知识。",
      "采用集质量、法规和设计于一体的综合方法。",
      "提供从概念到认证就绪文档的全程支持。",
      "在满足公告机构期望方面拥有直接经验。"
    ]
  },

  "post-market-surveillance": {
    title: "上市后监督",
    subtitle: "确保持续的安全与合规",
    description: "上市后监督是一项基本要求，旨在确保您的 IVD 或医疗器械投放市场后能够持续保障其安全性和有效性。",
    whatTitle: "为医疗器械和 IVD 制造商提供全面的解决方案",
    whatParagraphs: [
      "我们的专家团队将与您合作，根据您产品的独特需求，制定稳健且符合欧盟要求的 PMS 策略。",
      "我们还就<strong class='text-foreground'>变更控制流程</strong>提供指导，确保对您产品或文档的任何修改都能得到高效且完全合规的管理。"
    ],
    extraSections: [
      {
        title: "警戒报告与培训",
        paragraphs: [
          "有效的警戒是上市后安全性的基石。我们为制造商提供以下支持：",
          "• <strong class='text-foreground'>严重事件报告</strong>及后续纠正措施<br/>• <strong class='text-foreground'>建立定制的警戒流程</strong>，使其与您的质量保证体系保持一致<br/>• <strong class='text-foreground'>评估潜在警戒案例</strong>以确定其可报告性<br/>• 为您的团队提供<strong class='text-foreground'>全面的警戒培训</strong>"
        ]
      }
    ],
    whyTitle: "我们如何支持您的 PMS 需求",
    whyIntro: "我们的服务旨在简化合规流程并提升产品性能。",
    whyBenefits: [
      {
        title: "影响评估",
        text: "评估变更对您法规申报材料的影响，并确定所需的后续措施。"
      },
      {
        title: "流程开发",
        text: "开发流程以满足 IVDR 和 MDR 对上市后活动的严格要求。"
      },
      {
        title: "上市后跟进",
        text: "作为 PMCF (MD) 和 PMPF (IVD) 要求的一部分，开展研究、文献综述等工作。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "凭借对法规框架和行业最佳实践的深刻理解，我们提供专业的指导，确保您的 PMS 活动合规、有效，并融入您的日常运营。",
    partnerBullets: [
      "在 MDR 和 IVDR 上市后要求方面拥有深厚的专业知识。",
      "全面实施 PMS 和警戒系统。",
      "积极主动的合规与风险管理方法。",
      "为您的内部 PMS 团队提供培训和支持。"
    ]
  },

  "notified-body-submissions": {
    title: "向公告机构提交资料",
    subtitle: "您通往 CE 标志的途径",
    description: "对于医疗器械和 IVD 而言，向公告机构提交资料是获得 CE 标志的关键一步。我们能根据您的具体要求提供全面的支持。",
    whatTitle: "什么是向公告机构提交资料？",
    whatParagraphs: [
      "对于医疗器械和 IVD 而言，向公告机构提交资料是获得 <strong class='text-foreground'>CE 标志</strong>的关键一步。",
      "我们能根据您的具体要求提供全面的支持，确保您的文件符合公告机构的所有期望。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们为向公告机构提交资料的每个阶段提供专家支持。",
    whyBenefits: [
      {
        title: "资料提交准备",
        text: "全面汇编和审查提交的文件。"
      },
      {
        title: "专家指导",
        text: "我们的团队对法规要求和公告机构的期望有深入的了解。"
      },
      {
        title: "高效沟通",
        text: "在评估过程中，我们担当您的联络人，负责处理问询并解决问题。"
      },
      {
        title: "一次成功原则",
        text: "通过确保每一步的准确性和完整性，我们将延误降至最低。"
      },
      {
        title: "稳固的 NB 关系",
        text: "我们与公告机构的关系有助于加快互动速度，使评估过程更加顺畅。"
      }
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们在向公告机构提交资料方面拥有深厚的专业知识，可确保您顺利获得 CE 标志。",
    partnerBullets: [
      "在所有类别器械的 NB 提交方面拥有公认的成功记录。",
      "拥有与欧洲主要公告机构直接合作的经验。",
      "精通 MDR 和 IVDR 的提交要求。",
      "提供从差距评估到最终认证的端到端支持。"
    ]
  },

  "legal-representative": {
    title: "法定代表人",
    subtitle: "欧盟性能研究的法定代表",
    description: "对于总部设在欧盟以外的性能研究申办方来说，在欧盟境内指定一名法定代表人是一项法规要求。QbD Group 可提供专业的欧盟代表服务。",
    whatTitle: "法定代表人是什么？",
    whatParagraphs: [
      "法定代表人是在欧盟境内设立的自然人或法人，被指定代表非欧盟申办方处理与性能研究相关的法规事宜。",
      "对于总部设在欧盟以外的性能研究申办方来说，在欧盟境内指定一名法定代表人是一项法规要求。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "QbD Group 可以为您的研究提供必要的专业知识，引导您应对复杂的法规环境。",
    whyBenefits: [
      {
        title: "法规代表",
        text: "我们担任您在欧盟进行性能研究的法定代表人。"
      },
      {
        title: "向主管机构提交申请",
        text: "我们管理向国家主管机构和伦理委员会提交的申请。"
      },
      {
        title: "合规保证",
        text: "我们确保您的性能研究完全符合欧盟的法规要求。"
      },
      {
        title: "沟通枢纽",
        text: "我们充当申办方与欧盟法规机构之间的联络点。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "我们的法定代表人服务可确保您的性能研究顺利进入欧盟市场。",
    partnerBullets: [
      "在欧盟设立的实体，拥有深厚的法规专业知识。",
      "在多种治疗领域和器械类型方面拥有丰富经验。",
      "在管理欧盟法规互动方面拥有良好业绩记录。",
      "在申办方与主管机构之间实现无缝协调。"
    ]
  },

  "swiss-authorized-representative": {
    title: "瑞士授权代表 (CH-REP)",
    subtitle: "医疗器械瑞士市场准入",
    description: "总部位于瑞士境外的制造商必须指定一名瑞士授权代表，方可在瑞士市场销售医疗器械和 IVD 产品。",
    whatTitle: "什么是 CH-REP？",
    whatParagraphs: [
      "总部位于瑞士境外的制造商必须指定一名<strong class='text-foreground'>瑞士授权代表</strong>，方可在瑞士市场销售医疗器械和 IVD 产品。",
      "自新的瑞士《医疗器械条例》(MedDO) 和《体外诊断条例》(IvDO) 生效以来，指定 CH-REP 已成为强制性要求。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "Qarad Suiza 是 QbD Group 的一部分，可担任您值得信赖的瑞士授权代表。",
    whyBenefits: [
      {
        title: "瑞士市场代表",
        text: "我们担任您在瑞士市场官方指定的 CH-REP。"
      },
      {
        title: "文件验证",
        text: "我们验证您的技术文件和符合性声明是否符合瑞士的要求。"
      },
      {
        title: "与 Swissmedic 联络",
        text: "我们负责与 Swissmedic 的沟通并回应信息请求。"
      },
      {
        title: "警戒支持",
        text: "我们就纠正与安全行动与 Swissmedic 开展合作。"
      }
    ],
    partnerTitle: "为何选择 Qarad Suiza？",
    partnerIntro: "作为 QbD Group 的一部分，Qarad Suiza 是您值得信赖的合作伙伴，可助您进入瑞士市场。",
    partnerBullets: [
      "久经考验的专业经验：在医疗器械和 IVD 代表服务方面拥有超过 20 年的经验。",
      "法规可信度：在瑞士当地设有办事处，深入了解瑞士法规框架。",
      "独立代表：避免与瑞士进口商之间可能产生的利益冲突。",
      "全面支持：涵盖瑞士的特定要求、与 Swissmedic 的互动以及贯穿整个产品生命周期的咨询服务。"
    ]
  },

  "authorized-representative-eu-rep": {
    title: "欧盟授权代表 (EU-REP)",
    subtitle: "您在欧盟的法规合作伙伴",
    description: "如果您是欧盟/欧洲经济区以外的医疗器械或 IVD 制造商，根据法律要求，您必须指定一名欧盟授权代表，以确保符合 MDR 和 IVDR 的规定。",
    whatTitle: "什么是 EU-REP？哪些人需要 EU-REP？",
    whatParagraphs: [
      "EU-REP 是指在欧盟境内设立的自然人或法人，其已收到并接受了位于欧盟境外的制造商发出的书面授权。",
      "如果您是欧盟/欧洲经济区以外的医疗器械或 IVD 制造商，<strong class='text-foreground'>则必须依法指定欧盟授权代表</strong>，以确保符合 MDR 和 IVDR 的规定。",
      "<strong class='text-foreground'>哪些人需要 EU-REP？</strong>",
      "• 总部位于欧洲经济区 (EEE) 以外的制造商<br/>• 以自有品牌在欧盟市场销售器械的公司<br/>• 从美国、英国或亚洲扩展到欧洲的初创公司和中小型企业"
    ],
    helpCards: {
      heading: "作为您的 EU-REP，我们能为您提供哪些帮助？",
      intro: "QbD Group 总部位于比利时，在欧盟各地设有办事处，是与欧盟主管当局进行法规沟通的中心枢纽。",
      cards: [
        {
          title: "代表您与欧盟主管当局沟通",
          text: "作为官方代表，与欧盟所有相关法规机构进行沟通。"
        },
        {
          title: "文件验证",
          text: "验证您的欧盟符合性声明和技术文件是否已准备就绪。"
        },
        {
          title: "确保注册合规",
          text: "验证制造商是否已履行 MDR/IVDR 规定的注册义务。"
        },
        {
          title: "上市后支持",
          text: "为上市后监督、投诉、事故报告和现场安全纠正措施提供支持。"
        }
      ]
    },
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们提供全面的 EU-REP 服务，范围远超基本的代表职能。",
    whyBenefits: [
      {
        title: "全面代表",
        text: "作为官方代表，与欧盟所有相关法规机构进行沟通。"
      },
      {
        title: "文件验证",
        text: "验证您的符合性声明和技术文件。"
      },
      {
        title: "注册合规",
        text: "确保您已履行 MDR/IVDR 规定的所有注册义务。"
      },
      {
        title: "上市后支持",
        text: "为 PMS、投诉、事故报告和 FSCA 提供支持。"
      }
    ],
    partnerTitle: "为何选择 QbD Group 作为您的 EU-REP？",
    partnerIntro: "我们为全球 300 多家客户提供服务，是授权代表服务的领先供应商。",
    partnerBullets: [
      "全球 300 多家客户信赖我们的 EU-REP 服务。",
      "总部位于比利时，在欧盟各地设有办事处，可提供全面的法规覆盖。",
      "在医疗器械和 IVD 合规领域拥有 20 余年的经验。",
      "除代表职能外，还提供从战略、文件到 PMS 的端到端支持。"
    ]
  },

  "ukrp-uk-responsible-person": {
    title: "英国负责人 (UKRP)",
    subtitle: "进入英国医疗器械市场",
    description: "英国脱欧后，位于英国境外的制造商必须指定一名英国负责人，才能在大不列颠市场销售医疗器械和 IVD。",
    whatTitle: "什么是 UKRP？",
    whatParagraphs: [
      "英国脱欧后，位于英国境外的制造商必须指定一名<strong class='text-foreground'>英国负责人 (UKRP)</strong>，才能在大不列颠市场销售医疗器械和 IVD。",
      "UKRP 的角色是在英国脱欧后引入的，其职能与 MDR/IVDR 法规下的欧盟授权代表类似。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "Qarad UK Ltd. (QbD Group 旗下公司) 是您值得信赖的英国市场准入合作伙伴。",
    whyBenefits: [
      {
        title: "可靠的专业经验",
        text: "在医疗器械和 IVD 代表领域拥有超过 20 年的经验。"
      },
      {
        title: "法规领域信誉",
        text: "作为 UKRPA 成员，我们推崇专业行为和专业能力。"
      },
      {
        title: "独立代表",
        text: "我们帮助您避免因指定英国进口商而可能产生的利益冲突。"
      },
      {
        title: "全方位支持",
        text: "提供英国特定要求的指导、管理与 MHRA 的沟通以及法规咨询。"
      },
      {
        title: "双市场协调",
        text: "通过单一、值得信赖的合作伙伴，支持您的欧盟和英国双市场准入策略。"
      }
    ],
    processTitle: "我们的三步入门流程",
    processIntro: "选择 Qarad UK 作为您的 UKRP，流程十分简单：",
    processSteps: [
      {
        title: "1. 授权与设置",
        text: "我们正式签署 UKRP 协议并收集所需文件。"
      },
      {
        title: "2. 合规性审查",
        text: "我们评估提交给 MHRA 的文件是否准备就绪。"
      },
      {
        title: "3. 正式代表",
        text: "我们注册您的产品并履行 UKRP 的职责。"
      }
    ],
    partnerTitle: "为何选择 Qarad UK Ltd.?",
    partnerIntro: "QbD Group 旗下公司 Qarad UK Ltd. 是您值得信赖的英国市场准入合作伙伴。",
    partnerBullets: [
      "可靠的专业经验：在医疗器械和 IVD 代表领域拥有超过 20 年的经验。",
      "法规领域信誉：作为 UKRPA 成员，我们推崇专业行为和专业能力。",
      "独立代表：避免与英国进口商产生利益冲突。",
      "全方位支持：满足英国特定要求，管理与 MHRA 的沟通，并提供覆盖整个产品生命周期的咨询。",
      "双市场协调：通过单一、值得信赖的合作伙伴，支持您的欧盟和英国双市场准入策略。"
    ]
  },

  "person-responsible-for-regulatory-compliance": {
    title: "法规合规负责人 (PRRC)",
    subtitle: "欧盟 MDR 和 IVDR 的强制性要求",
    description: "为了在欧盟市场销售产品，医疗器械和 IVD 制造商必须正式指定一名 PRRC。如果没有正式指定的 PRRC，公司将无法在欧盟销售其产品。",
    whatTitle: "什么是 PRRC？",
    whatParagraphs: [
      "为了在欧盟市场销售产品，医疗器械和/或 IVD 制造商必须正式指定一名 PRRC（法规合规负责人）。根据欧盟法规，该职位在 <strong class='text-foreground'>MDR 下是一项强制性要求</strong>，并同样适用于 <strong class='text-foreground'>IVDR</strong>。",
      "如果没有正式指定的 PRRC，公司将无法在欧盟销售其产品。",
      "小型和微型企业（员工少于 50 人，年营业额和/或资产负债低于 1000 万欧元）通常难以聘请到合格的 RAQS 人才。将此项服务外包给我们的 QbD Group 专家可提供一种高效的解决方案。"
    ],
    extraSections: [
      {
        title: "PRRC 的职责",
        paragraphs: [
          "PRRC 必须：",
          "• 确保符合欧盟法规<br/>• 在产品投放市场前，审查 QMS 和技术文件<br/>• 接收有关审核、QMS/产品变更、投诉、召回或 FSCA 的通知<br/>• 为公司提供永久且持续的服务",
          "<strong class='text-foreground'>IVD 制造商的额外责任</strong>",
          "对于 IVD 制造商，特别是欧洲经济区 (EEA) 以外的制造商，PRRC 还必须确保欧盟授权代表 (EU-REP) 履行其义务。"
        ]
      }
    ],
    whyTitle: "我们如何为您提供支持？",
    whyIntro: "我们根据您的需求提供定制化的 PRRC 服务：",
    whyBenefits: [
      {
        title: "医疗器械 PRRC",
        text: "我们为小型或微型企业担任您的 PRRC。我们就 PRRC 的指定和任务的法规要求提供咨询。"
      },
      {
        title: "IVD PRRC",
        text: "我们为小型或微型企业担任您的 PRRC。我们担任欧盟授权代表 (EU-REP) 的 PRRC。"
      },
      {
        title: "法规咨询",
        text: "关于 PRRC 指定及相关任务的法规要求的专家咨询。"
      },
      {
        title: "程序支持",
        text: "协助起草或审查与 PRRC 职责相关的程序。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "为确保顺利进入欧洲市场，选择一位经验丰富的法规合规负责人至关重要，该负责人可以指导您完成整个流程。",
    partnerBullets: [
      "QbD Group 的专家可以担任您的外包 PRRC，帮助您的公司遵守欧盟法规。",
      "凭借超过 20 年的行业经验，我们能以高性价比的方式，确保您从外包的卓越中心获益。",
      "指定 QbD Group PRRC 的流程结构完善，并基于被指定人员的深入参与。"
    ]
  },

  "linguistic-review": {
    title: "语言审核",
    subtitle: "在紧迫的期限内提供准确合规的翻译",
    description: "语言审核过程中的最大挑战之一是翻译的交付周期极短。QbD Group 确保提供高质量的翻译，并满足程序性截止日期。",
    whatTitle: "我们面临哪些挑战？",
    whatParagraphs: [
      "语言审核过程中的最大挑战之一是翻译的<strong class='text-foreground'>交付周期极短</strong>。在 CHMP 就上市许可申请 (MAA) 发表意见后，您只有 <strong class='text-foreground'>5 个日历日</strong>的时间向成员国提交翻译文件。",
      "对于维护（变更、续展、上市后措施），翻译文件必须在 CHMP 发表意见或欧盟委员会做出决定后的 <strong class='text-foreground'>14 个日历日</strong>内提交。",
      "无论是为新申请准备英文版产品信息 (PI) 并在产品的整个生命周期内进行维护，还是确保在 25 种欧盟/欧洲经济区语言中提供准确的翻译，每一步都需要精确无误。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "在 QbD Group，我们可以帮助您满足这些法规要求，确保提供高质量的翻译并满足程序性截止日期。",
    whyBenefits: [
      {
        title: "多语言翻译",
        text: "在 25 种欧盟/欧洲经济区语言中提供具备法规精确性的准确翻译。"
      },
      {
        title: "紧迫期限管理",
        text: "经证实在 5 天和 14 天的严格法规期限内有交付能力。"
      },
      {
        title: "产品信息管理",
        text: "从初始创建到维护的 PI 全生命周期管理。"
      },
      {
        title: "质量保证",
        text: "严格的质量控制，确保一致性和法规合规性。"
      }
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "我们的语言审核团队将法规专业知识与语言精确性相结合。",
    partnerBullets: [
      "在满足紧迫的法规翻译期限方面拥有良好记录。",
      "服务覆盖 25 种欧盟/欧洲经济区语言。",
      "深入了解制药术语和法规要求。",
      "与您的法规申报流程无缝集成。"
    ]
  },

  "mdr-transition-support": {
    title: "MDD 到 MDR 的过渡支持",
    subtitle: "弥合 MDD 与 MDR 之间的差距",
    description: "从 MDD 到 MDR 的转变是医疗器械行业几十年来面临的最重大的法规变革之一。QbD Group 帮助您顺利完成这一过渡。",
    whatTitle: "为何尽早行动至关重要",
    whatParagraphs: [
      "从 MDD 到 MDR 的转变是医疗器械行业几十年来面临的最重大的法规变革之一。MDD 允许更大的灵活性，而 MDR 则对安全性、临床证据和可追溯性提出了更严格的标准。",
      "<strong class='text-foreground'>《医疗器械法规》(MDR) 不仅仅是一套新规则，更是一个全新的竞争环境。</strong> 更严格的审查、更多的文件要求以及对临床证据更高的期望，已经改变了医疗器械进入欧盟市场的方式。"
    ],
    whyTitle: "我们如何为您提供支持",
    whyIntro: "在 QbD Group，我们通过专业的指导和实践支持，帮助医疗器械公司弥合从 MDD 到 MDR 的差距。",
    whyBenefits: [
      {
        title: "差距评估",
        text: "全面评估您当前的 MDD 文件，以对照 MDR 的要求。"
      },
      {
        title: "过渡策略",
        text: "为您的 MDR 过渡之旅提供清晰、可行的路线图。"
      },
      {
        title: "文件更新",
        text: "更新您的技术文件，以符合 MDR 标准。"
      },
      {
        title: "与 NB 协调",
        text: "管理与公告机构的互动并规划提交事宜。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作",
    partnerIntro: "无论您是在处理原有产品的规则，还是在准备首次 MDR 提交，我们都能帮助您尽可能顺利、成功地完成过渡。",
    partnerBullets: [
      "在成功实现 MDD 到 MDR 过渡方面拥有良好的业绩记录。",
      "拥有涵盖 QA、RA 和技术文件的跨职能专业知识。",
      "采用务实的方法，与您的产品组合和业务目标保持一致。",
      "在应对公告机构的期望和要求方面拥有直接经验。"
    ]
  },

  "ivdr-transition-support": {
    title: "IVDD 到 IVDR 的过渡支持",
    subtitle: "助您自信应对 IVDD 到 IVDR 的过渡",
    description: "平均认证周期为 13 至 18 个月，且过渡截止日期日益临近，IVD 制造商不能再有任何拖延。提前规划不仅能确保合规性，还能为您带来竞争优势。",
    whatTitle: "为何尽早行动是关键",
    whatParagraphs: [
      "平均认证周期为 13 至 18 个月，且过渡截止日期日益临近，IVD 制造商不能再有任何拖延。提前规划不仅能确保合规性，还能为您带来竞争优势。",
      "如果没有明确的计划、充足的资源和合规的文档，即使是根基深厚的企业也面临着落后的风险。"
    ],
    helpCards: {
      heading: "受影响的关键领域",
      intro: "从 IVDD 到 IVDR 的过渡将影响您组织的多个领域：",
      cards: [
        {
          title: "质量保证",
          text: "ISO 13485 证书并不能保证符合 IVDR。您需要创建/更新相关流程、程序和模板。"
        },
        {
          title: "Regulatory Affairs",
          text: "确定法规策略、范围内的产品类别以及向公告机构 (NB) 的提交策略。"
        },
        {
          title: "技术文档",
          text: "必须满足 IVDR 的新要求。编制可供 NB 审核的 STED。"
        },
        {
          title: "临床与医学写作",
          text: "性能评估计划与研究。科学有效性与分析性能。"
        }
      ]
    },
    whyTitle: "我们的 IVDR 过渡方法",
    whyIntro: "我们为您提供从初步评估到最终提交的全程支持。",
    whyBenefits: [
      {
        title: "差距评估",
        text: "进行全面的差距评估和模拟审核，以识别需要改进的领域。"
      },
      {
        title: "QMS 更新",
        text: "提供符合 IVDR 的 SOP 及质量管理体系更新。"
      },
      {
        title: "法规路线图",
        text: "制定清晰的法规路线图，包括 NB 选择、规划和提交策略。"
      },
      {
        title: "文档支持",
        text: "技术文档、STED 编制及提交支持。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "我们将深厚的法规专业知识与行业实践经验相结合，助您满足 IVDR 的各项要求。",
    partnerBullets: [
      "在成功完成 IVDR 过渡方面拥有备受认可的往绩。",
      "提供从差距评估到 NB 提交的端到端支持。",
      "跨职能专业知识，涵盖 QA、RA、技术文档和临床领域。",
      "拥有与欧洲主要公告机构合作的直接经验。"
    ]
  },

  "coaching-training": {
    title: "法规指导与培训",
    subtitle: "在您的团队内部培养实用的法规能力",
    description: "QbD Group 提供定制化培训项目，旨在加强您的团队对 MDR/IVDR 的理解和实际法规应用能力。",
    whatTitle: "什么是法规指导与培训？",
    whatParagraphs: [
      "应对医疗器械和 IVD 日新月异的法规环境需要<strong class='text-foreground'>专业的知识</strong>。无论您的团队是 MDR/IVDR 的新手，还是需要深化现有专业知识，有针对性的指导和培训都能显著加快您的法规准备工作。",
      "QbD Group 提供<strong class='text-foreground'>定制化培训项目</strong>，旨在帮助您在组织内部建立实用的法规能力。我们的培训不止于理论，而是专注于实际应用，尽可能使用您自己的产品和文件作为案例研究。",
      "从为期一天的入门研讨会到为期数周的深度项目，我们的培训旨在满足您团队的需求并配合您的日程安排。"
    ],
    helpCards: {
      heading: "培训主题",
      intro: "我们的培训内容涵盖医疗器械和 IVD 的广泛法规主题：",
      cards: [
        {
          title: "MDR 和 IVDR 基础",
          text: "欧洲医疗器械和体外诊断法规概览。"
        },
        {
          title: "技术文件",
          text: "关于根据 MDR/IVDR 编写和审查技术文件的实用指南。"
        },
        {
          title: "上市后监督与警戒",
          text: "MDR/IVDR 下的 PMS 要求和警戒流程简介。"
        },
        {
          title: "质量管理体系",
          text: "关于 ISO 13485 以及 MDR/IVDR 下 QMS 期望的培训。"
        }
      ]
    },
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们的指导和培训服务旨在加强您团队对法规的理解和实际应用能力。",
    whyBenefits: [
      {
        title: "定制化项目",
        text: "我们根据您的产品、市场以及团队的经验水平量身定制培训内容。"
      },
      {
        title: "专家讲师",
        text: "课程由在行业内拥有丰富实践经验的法规专业人士授课。"
      },
      {
        title: "实践方法",
        text: "我们采用真实示例和案例研究，并在适当且经同意的情况下，使用客户的特定材料。"
      },
      {
        title: "巩固学习",
        text: "我们设定学习目标，并可提供可选的评估或跟进课程，以促进知识的吸收和保留。"
      },
      {
        title: "持续支持",
        text: "我们可以在培训后提供跟进课程和问答支持，以巩固关键主题。"
      }
    ],
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "QbD Group 的培训项目建立在医疗科技法规领域的丰富实践经验之上。",
    partnerBullets: [
      "拥有为初创公司、中小型企业和跨国制造商提供法规培训的丰富经验。",
      "培训内容可适应不同组织成熟度和产品类型。",
      "灵活的授课形式，包括现场、线上或混合模式的课程。",
      "培训课程结束后可提供可选的支持和指导。"
    ]
  },

  "materiovigilance": {
    title: "警戒",
    subtitle: "医疗器械和 IVD 的事件监测与报告",
    description: "警戒是指对上市后的医疗器械和 IVD 相关事件进行系统性的监测、评估和报告。QbD Group 协助制造商实施合规且高效的警戒系统。",
    whatTitle: "什么是警戒？",
    whatParagraphs: [
      "警戒包括对上市后医疗器械和 IVD 相关<strong class='text-foreground'>事件进行系统性的监测、评估和报告</strong>。根据欧洲医疗器械法规 (MDR) 和体外诊断法规 (IVDR)，这是上市后监督 (PMS) 义务的一部分。",
      "制造商必须实施警戒流程，以检测、评估严重事件、现场安全纠正措施 (FSCA) 和趋势报告，并向相关主管部门报告。<strong class='text-foreground'>不合规可能导致监管行动，包括召回或其他强制措施。</strong>",
      "QbD Group 协助制造商开发和维护符合 MDR 和 IVDR 要求的警戒系统，并将其整合到其全球质量和法规框架中。"
    ],
    helpCards: {
      heading: "核心警戒义务",
      intro: "根据 MDR 和 IVDR 的规定，制造商必须实施涵盖以下方面的流程：",
      cards: [
        {
          title: "严重事件报告",
          text: "在适用的法规期限内，及时向主管部门报告严重事件。"
        },
        {
          title: "趋势报告",
          text: "识别并报告非严重事件中具有统计学意义的趋势。"
        },
        {
          title: "现场安全纠正措施 (FSCA)",
          text: "规划、协调和执行纠正措施，包括在适当时通过现场安全通告进行沟通。"
        },
        {
          title: "警戒系统维护",
          text: "在 PMS 框架内建立并维护一个系统性流程，用于事件的检测、评估和报告。"
        }
      ]
    },
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们的警戒专家在警戒流程的关键领域提供结构化支持。",
    whyBenefits: [
      {
        title: "警戒系统设计",
        text: "我们支持设计和实施适合您组织和产品组合的警戒系统。"
      },
      {
        title: "事件评估",
        text: "我们支持根据监管报告标准对事件进行评估，并准备合规的提交文件。"
      },
      {
        title: "与监管机构沟通",
        text: "我们支持与主管部门的互动，包括准备和提交事件报告及安全通告。"
      },
      {
        title: "根本原因分析",
        text: "我们支持根本原因调查以及纠正和预防措施的制定。"
      },
      {
        title: "警戒文件",
        text: "我们支持警戒相关程序、模板和文档的开发与维护。"
      }
    ],
    partnerTitle: "您值得信赖的合作伙伴",
    partnerIntro: "QbD Group 将法规专业知识与医疗器械和 IVD 警戒流程的实践经验相结合。",
    partnerBullets: [
      "在支持 MDR 和 IVDR 下的警戒活动方面经验丰富。",
      "亲身参与事件管理和 FSCA 流程。",
      "采用综合方法，将警戒、PMS 和质量管理体系联系起来。",
      "灵活的支持模式，从一次性协助到更广泛的警戒支持。",
      "提供培训以支持内部能力建设。"
    ]
  },

  "mdr-compliance-ce-certification-for-medical-devices": {
    title: "MDR 合规与 CE 认证",
    subtitle: "为您的医疗器械实现并维持 MDR 合规",
    description: "欧盟医疗器械法规为安全性、质量和透明度设立了更高标准。无论您是计划将新器械推向市场，还是对现有器械进行过渡，我们都将全程为您提供指导。",
    whatTitle: "MDR 合规的真正影响",
    whatParagraphs: [
      "MDR 合规不仅仅是获得认证。它还关乎保障患者安全、业务连续性以及长期的市场准入。",
      "欧盟医疗器械法规 (MDR UE 2017/745) 为安全性、质量和透明度设立了更高标准。无论您是要将新器械推向市场，还是对现有器械进行过渡，实现并维持 MDR 合规都需要在 <strong class='text-foreground'>质量保证</strong>、<strong class='text-foreground'>Regulatory Affairs</strong> 和 <strong class='text-foreground'>技术文档</strong> 方面采取综合性方法。"
    ],
    helpCards: {
      heading: "常见挑战",
      intro: "在通往 MDR 合规的道路上，许多公司都面临着挑战：",
      cards: [
        {
          title: "监管监督日趋加强",
          text: "法规解读不断演变，公告机构的审查日益严格。"
        },
        {
          title: "QMS 缺陷",
          text: "质量管理体系中的漏洞可能会妨碍认证。"
        },
        {
          title: "临床要求提高",
          text: "证据要求提高及持续监控的需求加重了工作负担。"
        },
        {
          title: "资源压力",
          text: "管理文档、审计和申报给内部团队带来了巨大压力。"
        }
      ]
    },
    whyTitle: "我们如何支持您的 MDR 合规之路",
    whyIntro: "QbD Group 将在 MDR 流程的每一步为您提供指导，确保您顺利通过合规认证。",
    whyBenefits: [
      {
        title: "质量保证",
        text: "QMS 差距评估、补救措施支持、模拟审计和主导供应商审计。"
      },
      {
        title: "Regulatory Affairs",
        text: "法规策略、与 NB 协作、PRRC 支持以及授权代表服务。"
      },
      {
        title: "技术文档",
        text: "满足所有 MDR 要求，编制可随时提交给 NB 的文档。"
      },
      {
        title: "临床证据",
        text: "在生成临床证据以及必要时开展额外研究方面提供支持。"
      }
    ],
    partnerTitle: "为何选择 QbD Group？",
    partnerIntro: "与 QbD Group 合作，自信应对 MDR 挑战，更快地将您的器械推向市场。",
    partnerBullets: [
      "拥有超过 10 年支持欧洲各地医疗科技公司的经验。",
      "在 Regulatory Affairs、临床和质量领域拥有深厚的专业知识。",
      "与公告机构和行业协会直接合作。",
      "在 CE 标志认证和成功的 MDR 申报方面拥有良好往绩。"
    ]
  },

  "liaison-with-notified-bodies": {
    title: "与公告机构联络",
    subtitle: "与公告机构互动的战略性管理",
    description: "与公告机构互动是医疗器械和 IVD 合格评定流程中的一个关键步骤。QbD Group 在每个阶段为制造商提供支持，帮助确保沟通结构化、清晰，并与战略保持一致。",
    whatTitle: "什么是与公告机构联络？",
    whatParagraphs: [
      "与公告机构互动是医疗器械和 IVD 合格评定流程中的<strong class='text-foreground'>重要一步</strong>。与公告机构互动的质量和时机可能会对获得 CE 标志的进程产生重大影响。",
      "许多制造商低估了与公告机构互动的复杂性。从初步接触和提交申请，到回答审评问题以及管理审核后续工作，每一步都需要<strong class='text-foreground'>法规关注和精心准备的沟通</strong>。",
      "QbD Group 为制造商与公告机构的互动提供支持，帮助确保沟通清晰、完整，并与整体法规策略保持一致。"
    ],
    helpCards: {
      heading: "关键支持领域",
      intro: "我们与公告机构的联络服务涵盖所有互动环节：",
      cards: [
        {
          title: "初步接洽",
          text: "我们支持您选择合适的公告机构，并准备初步申请资料包。"
        },
        {
          title: "问询管理",
          text: "我们协调并支持您准备对公告机构问题的答复，帮助确保答复及时且结构良好。"
        },
        {
          title: "审核准备",
          text: "我们支持您的团队为公告机构的审核做准备，包括协助模拟审核和准备活动。"
        },
        {
          title: "持续联络",
          text: "我们在整个产品生命周期中支持您与公告机构进行结构化沟通，帮助确保连续性和清晰度。"
        }
      ]
    },
    whyTitle: "我们如何为您提供支持",
    whyIntro: "我们的团队在支持与公告机构的互动方面拥有丰富的经验，在整个流程中促进结构化和高效的沟通。",
    whyBenefits: [
      {
        title: "战略性 NB 选择",
        text: "我们指导您根据产品的预期用途和风险分类，选择最适合的公告机构。"
      },
      {
        title: "申请准备",
        text: "我们支持您准备申请资料包，帮助确保其完整、结构良好，并符合公告机构的期望。"
      },
      {
        title: "时间节点管理",
        text: "我们帮助规划和监督关键里程碑，支持及时的跟进，帮助您在审评过程中保持进度。"
      },
      {
        title: "审核准备",
        text: "我们进行差距评估，并可协助进行模拟审核，帮助您为公告机构的评估做好准备。"
      },
      {
        title: "沟通协调",
        text: "我们支持与公告机构的沟通协调，帮助确保清晰、一致和及时的答复。"
      }
    ],
    partnerTitle: "您值得信赖的合作伙伴",
    partnerIntro: "QbD Group 在支持制造商与公告机构就医疗器械和 IVD 进行互动方面拥有丰富经验。",
    partnerBullets: [
      "拥有与多个欧洲公告机构合作的经验。",
      "深刻理解 MDR 和 IVDR 下的合格评定要求。",
      "提供结构化的项目支持，以促进和协调与公告机构的互动。",
      "灵活的合作模式，从一次性的特定支持到更广泛的项目参与。"
    ]
  },

};

export const getRAOfferingI18n = (
  contentKey: string,
  lang: SiteLang
): RAOfferingI18n | undefined => {
  if (lang === "en") return undefined;
  if (lang === "es") return raOfferingsEs[contentKey];
  if (lang === "zh-cn") return raOfferingsZh[contentKey];
  return undefined;
};
